import { Schema, Stream, Effect, Queue } from 'effect';
import { defineMessageUnion } from 'foldkit/message';
import { HoverSource, Completion, CompletionItem, CompletionPopup, HoverPopup, diagnosticsAt, mostSevere, offsetAtPoint, isOver, optionId, segments } from '@foldworks/text-intelligence';
import { History } from '@foldworks/history';
import { Mount, Command } from 'foldkit';
import { afterCommit } from 'foldkit/render';
import { defineView } from 'foldkit/submodel';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/outliner.ts
var outliner_exports = {};
__export(outliner_exports, {
  Message: () => Message2,
  Model: () => Model2,
  init: () => init,
  update: () => update,
  view: () => view
});
var Action = Schema.Literals([
  "Split",
  "Indent",
  "Outdent",
  "MoveUp",
  "MoveDown",
  "Collapse",
  "Expand",
  "ToggleChecked",
  "ZoomIn",
  "ZoomOut",
  "Undo",
  "Redo",
  "MergePrevious",
  "MergeNext",
  "Delete",
  "FocusPrevious",
  "FocusNext",
  "FocusPreviousEnd",
  "FocusNextStart",
  "SelectRow",
  "ExtendUp",
  "ExtendDown",
  "SelectPrevious",
  "SelectNext",
  "CollapseOrParent",
  "ExpandOrChild",
  "Edit",
  "ClearSelection",
  "SelectAll",
  "ShowInfo",
  "Complete"
]);
var FOCUS_KEEPING = /* @__PURE__ */ new Set([
  "Indent",
  "Outdent",
  "Collapse",
  "Expand",
  "ToggleChecked",
  "ShowInfo",
  "Complete"
]);
var keepsFocus = (action) => FOCUS_KEEPING.has(action);
var modifiers = (event, platform) => platform === "mac" ? { mod: event.metaKey, shift: event.shiftKey, alt: event.altKey, ctrl: event.ctrlKey } : {
  mod: event.ctrlKey,
  shift: event.shiftKey,
  alt: event.altKey,
  ctrl: event.metaKey
};
var only = (m, ...wanted) => ["mod", "shift", "alt", "ctrl"].every((name) => m[name] === wanted.includes(name));
var isKey = (event, key, code) => event.key === key || code !== void 0 && event.code === code;
var structural = (event, m, platform) => {
  if (event.key === "Tab" && only(m)) return "Indent";
  if (event.key === "Tab" && only(m, "shift")) return "Outdent";
  if (isKey(event, "]", "BracketRight") && only(m, "mod")) return "Indent";
  if (isKey(event, "[", "BracketLeft") && only(m, "mod")) return "Outdent";
  const vertical = event.key === "ArrowUp" ? "Up" : event.key === "ArrowDown" ? "Down" : void 0;
  if (vertical !== void 0) {
    if (only(m, "mod", "shift") || platform === "mac" && only(m, "mod", "ctrl")) {
      return vertical === "Up" ? "MoveUp" : "MoveDown";
    }
    if (only(m, "mod")) return vertical === "Up" ? "Collapse" : "Expand";
  }
  if (isKey(event, ".", "Period") && only(m, "mod")) return "ZoomIn";
  if ((isKey(event, ".", "Period") || event.key === ">") && only(m, "mod", "shift")) {
    return "ZoomOut";
  }
  if (event.key === "Enter" && only(m, "mod")) return "ToggleChecked";
  if (event.key.toLowerCase() === "z" && only(m, "mod")) return "Undo";
  if (event.key.toLowerCase() === "z" && only(m, "mod", "shift")) return "Redo";
  if (event.key.toLowerCase() === "y" && platform === "other" && only(m, "mod")) return "Redo";
  return void 0;
};
var resolveKey = (event, context, platform) => {
  if (event.isComposing) return void 0;
  const m = modifiers(event, platform);
  const shared = structural(event, m, platform);
  if (shared !== void 0) return shared;
  if (context.mode === "Rows") {
    if (event.key === "ArrowUp" && only(m)) return "SelectPrevious";
    if (event.key === "ArrowDown" && only(m)) return "SelectNext";
    if (event.key === "ArrowUp" && only(m, "shift")) return "ExtendUp";
    if (event.key === "ArrowDown" && only(m, "shift")) return "ExtendDown";
    if (event.key === "ArrowLeft" && only(m)) return "CollapseOrParent";
    if (event.key === "ArrowRight" && only(m)) return "ExpandOrChild";
    if ((event.key === "Enter" || event.key === "F2") && only(m)) return "Edit";
    if (event.key === " " && only(m)) return "ToggleChecked";
    if (event.key === "Escape" && only(m)) return "ClearSelection";
    if ((event.key === "Backspace" || event.key === "Delete") && only(m)) return "Delete";
    if (event.key.toLowerCase() === "a" && only(m, "mod")) return "SelectAll";
    return void 0;
  }
  const { caret } = context;
  const collapsedCaret = caret.start === caret.end;
  if (event.key === " " && only(m, platform === "mac" ? "ctrl" : "mod", "shift")) return "ShowInfo";
  if (event.key === " " && only(m, platform === "mac" ? "ctrl" : "mod")) return "Complete";
  if (event.key === "Enter" && only(m)) return "Split";
  if (event.key === "Escape" && only(m)) return "SelectRow";
  if (event.key === "Backspace" && only(m, "mod", "shift")) return "Delete";
  if (event.key === "Backspace" && only(m) && collapsedCaret && caret.start === 0) {
    return "MergePrevious";
  }
  if (event.key === "Delete" && only(m) && collapsedCaret && caret.end === caret.length) {
    return "MergeNext";
  }
  if (event.key === "ArrowUp" && only(m) && collapsedCaret && caret.onFirstLine) {
    return "FocusPrevious";
  }
  if (event.key === "ArrowDown" && only(m) && collapsedCaret && caret.onLastLine) {
    return "FocusNext";
  }
  if (event.key === "ArrowLeft" && only(m) && collapsedCaret && caret.start === 0) {
    return "FocusPreviousEnd";
  }
  if (event.key === "ArrowRight" && only(m) && collapsedCaret && caret.end === caret.length) {
    return "FocusNextStart";
  }
  if (event.key === "ArrowUp" && only(m, "shift") && caret.start === 0 && caret.onFirstLine) {
    return "ExtendUp";
  }
  if (event.key === "ArrowDown" && only(m, "shift") && caret.end === caret.length && caret.onLastLine) {
    return "ExtendDown";
  }
  if (event.key.toLowerCase() === "a" && only(m, "mod") && caret.start === 0 && caret.end === caret.length) {
    return "SelectAll";
  }
  return void 0;
};
var shortcutHelp = (platform) => {
  const mod = platform === "mac" ? "\u2318" : "Ctrl+";
  const shift = platform === "mac" ? "\u21E7" : "Shift+";
  return [
    { keys: "Return", label: "New item, or split at the caret" },
    { keys: `${shift}Return`, label: "Line break inside an item" },
    { keys: `Tab \xB7 ${shift}Tab`, label: "Indent \xB7 outdent" },
    { keys: `${mod}${shift}\u2191 \xB7 ${mod}${shift}\u2193`, label: "Move up \xB7 down" },
    { keys: `${mod}\u2191 \xB7 ${mod}\u2193`, label: "Collapse \xB7 expand" },
    { keys: `${mod}. \xB7 ${mod}${shift}.`, label: "Hoist \xB7 unhoist" },
    { keys: `${mod}Return`, label: "Mark done" },
    { keys: "Esc", label: "Select rows; Return to edit again" },
    { keys: `${shift}\u2191 \xB7 ${shift}\u2193`, label: "Extend the row selection" },
    { keys: `${mod}Z \xB7 ${mod}${shift}Z`, label: "Undo \xB7 redo" },
    {
      keys: `${platform === "mac" ? "\u2303" : "Ctrl+"}${shift}Space`,
      label: "Show information at the caret"
    }
  ];
};
var Item = Schema.suspend(
  () => Schema.Struct({
    id: Schema.String,
    text: Schema.String,
    collapsed: Schema.Boolean,
    checked: Schema.Boolean,
    children: Schema.Array(Item)
  })
);
var Items = Schema.Array(Item);
var Document = Schema.Struct({ version: Schema.Literal(1), items: Items });
var item = (id, text = "", children = [], options = {}) => ({
  id,
  text,
  collapsed: options.collapsed ?? false,
  checked: options.checked ?? false,
  children
});
var walk = (items) => items.flatMap((node) => [node, ...walk(node.children)]);
var find = (items, id) => {
  for (const node of items) {
    if (node.id === id) return node;
    const found = find(node.children, id);
    if (found !== void 0) return found;
  }
  return void 0;
};
var locate = (items, id, parentId = null, depth = 0) => {
  for (const [index, node] of items.entries()) {
    if (node.id === id) return { parentId, index, depth };
    const found = locate(node.children, id, node.id, depth + 1);
    if (found !== void 0) return found;
  }
  return void 0;
};
var ancestors = (items, id) => {
  const path = [];
  const visit = (nodes) => nodes.some((node) => {
    if (node.id === id) return true;
    path.push(node.id);
    if (visit(node.children)) return true;
    path.pop();
    return false;
  });
  return visit(items) ? path : [];
};
var isWithin = (items, id, ancestorId) => ancestorId === null || id === ancestorId || ancestors(items, id).includes(ancestorId);
var childrenOf = (items, parentId) => parentId === null ? items : find(items, parentId)?.children;
var updateItem = (items, id, f) => {
  let changed = false;
  const next = items.map((node) => {
    if (node.id === id) {
      const updated = f(node);
      changed ||= updated !== node;
      return updated;
    }
    const children = updateItem(node.children, id, f);
    if (children === node.children) return node;
    changed = true;
    return { ...node, children };
  });
  return changed ? next : items;
};
var setChildren = (items, parentId, children) => parentId === null ? children : updateItem(items, parentId, (node) => ({ ...node, children }));
var visibleRows = (items, scopeId = null) => {
  const rows = [];
  const visit = (nodes, parentId, depth) => nodes.forEach((node, index) => {
    rows.push({
      id: node.id,
      text: node.text,
      depth,
      parentId,
      index,
      setSize: nodes.length,
      hasChildren: node.children.length > 0,
      collapsed: node.collapsed,
      checked: node.checked
    });
    if (!node.collapsed) visit(node.children, node.id, depth + 1);
  });
  visit(childrenOf(items, scopeId) ?? [], scopeId, 0);
  return rows;
};
var roots = (items, ids) => {
  const wanted = new Set(ids);
  const result = [];
  const visit = (nodes) => nodes.forEach((node) => {
    if (wanted.has(node.id)) result.push(node.id);
    else visit(node.children);
  });
  visit(items);
  return result;
};
var removeItems = (items, ids) => {
  const wanted = new Set(ids);
  const removed = [];
  const prune = (nodes) => {
    let changed = false;
    const kept = [];
    for (const node of nodes) {
      if (wanted.has(node.id)) {
        removed.push(node);
        changed = true;
        continue;
      }
      const children = prune(node.children);
      if (children !== node.children) changed = true;
      kept.push(children === node.children ? node : { ...node, children });
    }
    return changed ? kept : nodes;
  };
  return { items: prune(items), removed };
};
var insertItems = (items, parentId, index, inserted) => {
  const siblings = childrenOf(items, parentId);
  if (siblings === void 0) return void 0;
  const at = Math.max(0, Math.min(index, siblings.length));
  return setChildren(items, parentId, [
    ...siblings.slice(0, at),
    ...inserted,
    ...siblings.slice(at)
  ]);
};
var resolvePlacement = (items, placement) => {
  switch (placement._tag) {
    case "Start":
    case "End": {
      const siblings = childrenOf(items, placement.parentId);
      if (siblings === void 0) return void 0;
      const parentDepth = placement.parentId === null ? -1 : locate(items, placement.parentId)?.depth ?? -1;
      return {
        parentId: placement.parentId,
        index: placement._tag === "Start" ? 0 : siblings.length,
        depth: parentDepth + 1
      };
    }
    case "After":
    case "Before": {
      const location = locate(items, placement.siblingId);
      return location === void 0 ? void 0 : { ...location, index: location.index + (placement._tag === "After" ? 1 : 0) };
    }
  }
};
var moveItems = (items, ids, placement) => {
  const moving = roots(items, ids);
  if (moving.length === 0) return void 0;
  const anchor = placement._tag === "Start" || placement._tag === "End" ? placement.parentId : placement.siblingId;
  if (anchor !== null && moving.some((id) => isWithin(items, anchor, id))) return void 0;
  const { items: without, removed } = removeItems(items, moving);
  const target = resolvePlacement(without, placement);
  if (target === void 0) return void 0;
  const expanded = target.parentId === null ? without : updateItem(
    without,
    target.parentId,
    (node) => node.collapsed ? { ...node, collapsed: false } : node
  );
  const next = insertItems(expanded, target.parentId, target.index, removed);
  return next === void 0 || sameStructure(items, next) ? void 0 : next;
};
var sameStructure = (a, b) => a.length === b.length && a.every((node, index) => {
  const other = b[index];
  return node.id === other.id && node.collapsed === other.collapsed && sameStructure(node.children, other.children);
});
var indent = (items, ids) => {
  let current = items;
  const blocked = /* @__PURE__ */ new Set();
  for (const id of roots(items, ids)) {
    const location = locate(current, id);
    const siblings = location && childrenOf(current, location.parentId);
    const previous = location && location.index > 0 ? siblings?.[location.index - 1] : void 0;
    if (previous === void 0 || blocked.has(previous.id)) {
      blocked.add(id);
      continue;
    }
    current = moveItems(current, [id], { _tag: "End", parentId: previous.id }) ?? current;
  }
  return current === items ? void 0 : current;
};
var outdent = (items, ids, scopeId = null) => {
  let current = items;
  for (const id of roots(items, ids)) {
    const location = locate(current, id);
    if (location === void 0 || location.parentId === scopeId || location.parentId === null) {
      continue;
    }
    const parentId = location.parentId;
    const siblings = childrenOf(current, parentId);
    const following = siblings.slice(location.index + 1);
    const node = siblings[location.index];
    const adopted = following.length === 0 ? node : { ...node, collapsed: false, children: [...node.children, ...following] };
    const trimmed = setChildren(current, parentId, siblings.slice(0, location.index));
    const parentLocation = locate(trimmed, parentId);
    current = insertItems(trimmed, parentLocation.parentId, parentLocation.index + 1, [adopted]) ?? current;
  }
  return current === items ? void 0 : current;
};
var siblingRun = (items, ids) => {
  const moving = roots(items, ids);
  const locations = moving.map((id) => locate(items, id));
  const first = locations[0];
  if (first === void 0) return void 0;
  const contiguous = locations.every(
    (location, offset) => location !== void 0 && location.parentId === first.parentId && location.index === first.index + offset
  );
  return contiguous ? { parentId: first.parentId, first: first.index, last: first.index + moving.length - 1 } : void 0;
};
var moveUp = (items, ids, scopeId = null) => {
  const run = siblingRun(items, ids);
  if (run === void 0) return void 0;
  const moving = roots(items, ids);
  const siblings = childrenOf(items, run.parentId);
  if (run.first > 0) {
    return moveItems(items, moving, { _tag: "Before", siblingId: siblings[run.first - 1].id });
  }
  if (run.parentId === null || run.parentId === scopeId) return void 0;
  const parent = locate(items, run.parentId);
  const uncles = childrenOf(items, parent.parentId);
  const previousUncle = parent.index > 0 ? uncles[parent.index - 1] : void 0;
  return previousUncle === void 0 ? moveItems(items, moving, { _tag: "Before", siblingId: run.parentId }) : moveItems(items, moving, { _tag: "End", parentId: previousUncle.id });
};
var moveDown = (items, ids, scopeId = null) => {
  const run = siblingRun(items, ids);
  if (run === void 0) return void 0;
  const moving = roots(items, ids);
  const siblings = childrenOf(items, run.parentId);
  if (run.last < siblings.length - 1) {
    return moveItems(items, moving, { _tag: "After", siblingId: siblings[run.last + 1].id });
  }
  if (run.parentId === null || run.parentId === scopeId) return void 0;
  const parent = locate(items, run.parentId);
  const uncles = childrenOf(items, parent.parentId);
  const nextUncle = uncles[parent.index + 1];
  return nextUncle === void 0 ? moveItems(items, moving, { _tag: "After", siblingId: run.parentId }) : moveItems(items, moving, { _tag: "Start", parentId: nextUncle.id });
};
var split = (items, id, start, end, newId2) => {
  const node = find(items, id);
  const location = locate(items, id);
  if (node === void 0 || location === void 0) return void 0;
  const before = node.text.slice(0, start);
  const after = node.text.slice(end);
  if (start === 0 && end === 0 && node.text.length > 0) {
    const next2 = insertItems(items, location.parentId, location.index, [item(newId2)]);
    return next2 === void 0 ? void 0 : { items: next2, focusId: id, offset: 0 };
  }
  const updated = updateItem(items, id, (current) => ({ ...current, text: before }));
  const created = item(newId2, after);
  const next = node.children.length > 0 && !node.collapsed ? insertItems(updated, id, 0, [created]) : insertItems(updated, location.parentId, location.index + 1, [created]);
  return next === void 0 ? void 0 : { items: next, focusId: newId2, offset: 0 };
};
var mergeIntoPrevious = (items, id, scopeId = null) => {
  const rows = visibleRows(items, scopeId);
  const position = rows.findIndex((row) => row.id === id);
  const previous = position > 0 ? rows[position - 1] : void 0;
  const node = find(items, id);
  const location = locate(items, id);
  if (previous === void 0 || node === void 0 || location === void 0) return void 0;
  const offset = previous.text.length;
  let next = removeItems(items, [id]).items;
  next = updateItem(next, previous.id, (current) => ({
    ...current,
    text: current.text + node.text
  }));
  if (node.children.length > 0) {
    next = previous.id === location.parentId ? insertItems(next, location.parentId, location.index, node.children) ?? next : updateItem(next, previous.id, (current) => ({
      ...current,
      children: [...current.children, ...node.children]
    }));
  }
  return { items: next, focusId: previous.id, offset };
};
var mergeNext = (items, id, scopeId = null) => {
  const rows = visibleRows(items, scopeId);
  const position = rows.findIndex((row) => row.id === id);
  const next = position >= 0 ? rows[position + 1] : void 0;
  return next === void 0 ? void 0 : mergeIntoPrevious(items, next.id, scopeId);
};
var setCollapsed = (items, id, collapsed, recursive = false) => {
  const apply = (node) => {
    const children = recursive ? node.children.map(apply) : node.children;
    const value = node.children.length > 0 && collapsed;
    return value === node.collapsed && children === node.children ? node : { ...node, collapsed: value, children };
  };
  return updateItem(items, id, apply);
};
var setAllCollapsed = (items, collapsed) => {
  const apply = (nodes) => {
    let changed = false;
    const next = nodes.map((node) => {
      const children = apply(node.children);
      const value = node.children.length > 0 && collapsed;
      if (children === node.children && value === node.collapsed) return node;
      changed = true;
      return { ...node, collapsed: value, children };
    });
    return changed ? next : nodes;
  };
  return apply(items);
};
var expandToLevel = (items, level) => {
  const apply = (nodes, depth) => nodes.map((node) => ({
    ...node,
    collapsed: node.children.length > 0 && depth + 1 >= level,
    children: apply(node.children, depth + 1)
  }));
  return apply(items, 0);
};
var dropTarget = (rows, draggedIds, gap, depth, scopeId = null) => {
  const dragged = new Set(draggedIds);
  const byId = new Map(rows.map((row) => [row.id, row]));
  const hidden = (row) => {
    for (let id = row.id; id !== null; id = byId.get(id)?.parentId ?? null) {
      if (dragged.has(id)) return true;
    }
    return false;
  };
  const candidates = rows.filter((row) => !hidden(row));
  const at = Math.max(0, Math.min(gap, candidates.length));
  const above = candidates[at - 1];
  const below = candidates[at];
  if (above === void 0) {
    return { placement: { _tag: "Start", parentId: scopeId }, depth: 0, afterRowId: null };
  }
  const minimum = below?.depth ?? 0;
  const maximum = above.depth + 1;
  const clamped = Math.max(minimum, Math.min(maximum, Math.round(depth)));
  if (clamped === above.depth + 1) {
    return {
      placement: {
        _tag: above.collapsed && above.hasChildren ? "End" : "Start",
        parentId: above.id
      },
      depth: clamped,
      afterRowId: above.id
    };
  }
  let sibling = above;
  while (sibling.depth > clamped && sibling.parentId !== null) {
    const parent = byId.get(sibling.parentId);
    if (parent === void 0) break;
    sibling = parent;
  }
  return {
    placement: { _tag: "After", siblingId: sibling.id },
    depth: clamped,
    afterRowId: above.id
  };
};

// src/model.ts
var Focus = Schema.Struct({ id: Schema.String, start: Schema.Number, end: Schema.Number });
var RowSelection = Schema.Struct({ anchorId: Schema.String, headId: Schema.String });
var Placement = Schema.Union([
  Schema.TaggedStruct("Start", { parentId: Schema.NullOr(Schema.String) }),
  Schema.TaggedStruct("End", { parentId: Schema.NullOr(Schema.String) }),
  Schema.TaggedStruct("After", { siblingId: Schema.String }),
  Schema.TaggedStruct("Before", { siblingId: Schema.String })
]);
var DropTarget = Schema.Struct({
  placement: Placement,
  depth: Schema.Number,
  afterRowId: Schema.NullOr(Schema.String),
  /** The host's policy refuses a drop here; releasing does nothing. */
  refused: Schema.optional(Schema.Boolean)
});
var Drag = Schema.Struct({ ids: Schema.Array(Schema.String), target: Schema.NullOr(DropTarget) });
var Snapshot = Schema.Struct({ items: Items, focus: Schema.NullOr(Focus) });
var HoverTarget = Schema.Struct({ id: Schema.String, offset: Schema.Number, source: HoverSource });
var OpenCompletion = Schema.Struct({ id: Schema.String, ...Completion.List.fields });
var Mode = Schema.Literals(["Text", "Rows"]);
var Model = Schema.Struct({
  id: Schema.String,
  items: Items,
  /** The hoisted item whose children fill the outline, or `null` for the whole document. */
  scopeId: Schema.NullOr(Schema.String),
  mode: Mode,
  focus: Schema.NullOr(Focus),
  selection: Schema.NullOr(RowSelection),
  drag: Schema.NullOr(Drag),
  hover: Schema.NullOr(HoverTarget),
  completion: Schema.NullOr(OpenCompletion),
  history: History.Schema(Snapshot),
  /** When the last keystroke changed text, for grouping typing into undo steps. */
  typedAt: Schema.Number,
  nextId: Schema.Number,
  /** Increments on every document change, for hosts that persist the outline. */
  revision: Schema.Number,
  announcement: Schema.String
});
var counterFor = (id, items) => {
  const prefix = `${id}-`;
  return walk(items).reduce((highest, node) => {
    const suffix = node.id.startsWith(prefix) ? Number(node.id.slice(prefix.length)) : NaN;
    return Number.isSafeInteger(suffix) ? Math.max(highest, suffix) : highest;
  }, 0) + 1;
};
var init = (config) => {
  const items = config.items ?? [];
  const scopeId = config.scopeId !== void 0 && config.scopeId !== null && find(items, config.scopeId) ? config.scopeId : null;
  return {
    id: config.id,
    items,
    scopeId,
    mode: "Text",
    focus: null,
    selection: null,
    drag: null,
    hover: null,
    completion: null,
    history: History.init(),
    typedAt: 0,
    nextId: counterFor(config.id, items),
    revision: 0,
    announcement: ""
  };
};
var domIds = (modelId) => ({
  tree: `${modelId}-tree`,
  row: (id) => `${modelId}-row-${id}`,
  text: (id) => `${modelId}-text-${id}`,
  hover: `${modelId}-hover`,
  completion: `${modelId}-completion`,
  add: `${modelId}-add`
});

// src/message.ts
var Message = defineMessageUnion({
  /** A resolved key press. `start`/`end` are the caret; `goalX` is its horizontal position. */
  Pressed: { action: Action, id: Schema.String, start: Schema.Number, end: Schema.Number, goalX: Schema.Number },
  /** `time` is the event timestamp, used to group typing into undo steps. */
  EditedText: { id: Schema.String, text: Schema.String, start: Schema.Number, end: Schema.Number, time: Schema.Number },
  FocusedText: { id: Schema.String, start: Schema.Number, end: Schema.Number },
  PastedText: { id: Schema.String, mode: Mode, start: Schema.Number, end: Schema.Number, text: Schema.String },
  ToggledCollapsed: { id: Schema.String, recursive: Schema.Boolean },
  ToggledChecked: { id: Schema.String },
  ClickedBullet: { id: Schema.String },
  SelectedRange: { anchorId: Schema.String, headId: Schema.String },
  StartedDrag: { ids: Schema.Array(Schema.String) },
  MovedDrag: { target: Schema.NullOr(DropTarget) },
  Dropped: {},
  CancelledDrag: {},
  /** The pointer rested on a character of an item's text, or left the text. */
  Hovered: { target: Schema.NullOr(Schema.Struct({ id: Schema.String, offset: Schema.Number })) },
  DismissedHover: {},
  /** Ctrl+Space in an item's text. A host answers with `ShowCompletions`. */
  RequestedCompletion: { id: Schema.String, start: Schema.Number, end: Schema.Number },
  /**
   * Offers items that replace `from`–`to` of an item's text. Ignored unless the
   * caret is in that item and range. An empty list closes the suggestions.
   */
  ShowCompletions: {
    id: Schema.String,
    from: Schema.Number,
    to: Schema.Number,
    items: Schema.Array(CompletionItem)
  },
  /**
   * Typing into a placeholder, pressing Return on it, or clicking its marker.
   * Creates an item with `text` at `index` among the parent's children and puts
   * the caret at `offset`.
   */
  FilledPlaceholder: {
    parentId: Schema.NullOr(Schema.String),
    index: Schema.Number,
    key: Schema.String,
    text: Schema.String,
    offset: Schema.Number
  },
  MovedCompletion: { delta: Schema.Number },
  /**
   * `index` counts the suggestions shown, after narrowing by what was typed.
   * Omitted, the active suggestion is accepted.
   */
  AcceptedCompletion: { index: Schema.optional(Schema.Number) },
  DismissedCompletion: {},
  Hoisted: { id: Schema.NullOr(Schema.String) },
  ClickedAdd: {},
  SetAllCollapsed: { collapsed: Schema.Boolean },
  ExpandedToLevel: { level: Schema.Number },
  ClickedUndo: {},
  ClickedRedo: {},
  /** Replaces the document and clears history, for example after loading a file. */
  Load: { items: Items },
  /**
   * Replaces the document as one undoable step, for edits a host makes on the
   * outline's behalf. Focus stays where it is. Replacements that share a
   * `coalescingKey` undo together.
   */
  Replace: { items: Items, announcement: Schema.String, coalescingKey: Schema.optional(Schema.String) },
  /** Expands, unhoists if needed, and puts the caret at the end of an item. */
  Reveal: { id: Schema.String },
  CompletedFocus: {}
});

// src/caret.ts
var COPIED_STYLES = [
  "boxSizing",
  "width",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "fontVariant",
  "fontStretch",
  "letterSpacing",
  "lineHeight",
  "textTransform",
  "textIndent",
  "wordSpacing",
  "tabSize",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderTopStyle",
  "borderRightStyle",
  "borderBottomStyle",
  "borderLeftStyle",
  "overflowWrap",
  "wordBreak"
];
var MEASURE_LIMIT = 4e3;
var measure = (input) => {
  const value = input.value;
  if (value.length > MEASURE_LIMIT) return void 0;
  const doc = input.ownerDocument;
  const view2 = doc.defaultView;
  if (view2 === null) return void 0;
  const computed = view2.getComputedStyle(input);
  const mirror = doc.createElement("div");
  for (const name of COPIED_STYLES) mirror.style[name] = computed[name];
  Object.assign(mirror.style, {
    position: "absolute",
    visibility: "hidden",
    top: "0",
    left: "-10000px",
    height: "auto",
    overflow: "hidden",
    whiteSpace: "pre-wrap"
  });
  const spans = [];
  let index = 0;
  for (const character of value) {
    const span = doc.createElement("span");
    span.textContent = character;
    mirror.append(span);
    spans.push({ span, from: index, to: index + character.length });
    index += character.length;
  }
  const end = doc.createElement("span");
  end.textContent = "\u200B";
  mirror.append(end);
  doc.body.append(mirror);
  const tops = [];
  const lefts = [];
  for (const { span, from, to } of spans) {
    for (let position = from; position < to; position += 1) {
      tops[position] = span.offsetTop;
      lefts[position] = span.offsetLeft;
    }
  }
  tops[value.length] = end.offsetTop;
  lefts[value.length] = end.offsetLeft;
  mirror.remove();
  return { tops, lefts };
};
var contentLeft = (input) => input.getBoundingClientRect().left + input.clientLeft;
var caretLines = (input) => {
  const { selectionStart: start, selectionEnd: end, value } = input;
  const geometry = measure(input);
  if (geometry === void 0) {
    return {
      onFirstLine: !value.slice(0, start).includes("\n"),
      onLastLine: !value.slice(end).includes("\n"),
      x: contentLeft(input)
    };
  }
  const { tops, lefts } = geometry;
  return {
    onFirstLine: tops[start] === tops[0],
    onLastLine: tops[end] === tops[value.length],
    x: contentLeft(input) + (lefts[start] ?? 0)
  };
};
var offsetAtX = (input, x, line) => {
  const value = input.value;
  const geometry = measure(input);
  if (geometry === void 0) {
    return line === "First" ? 0 : value.length;
  }
  const { tops, lefts } = geometry;
  const top = line === "First" ? tops[0] : tops[value.length];
  const target = x - contentLeft(input);
  let best = line === "First" ? 0 : value.length;
  let distance = Infinity;
  for (let position = 0; position <= value.length; position += 1) {
    if (tops[position] !== top) continue;
    const candidate = Math.abs((lefts[position] ?? 0) - target);
    if (candidate < distance) {
      best = position;
      distance = candidate;
    }
  }
  return best;
};

// src/policy.ts
var placesOf = (items) => {
  const places = /* @__PURE__ */ new Map();
  const visit = (nodes, parentId) => nodes.forEach((node, index) => {
    places.set(node.id, { parentId, index });
    visit(node.children, node.id);
  });
  visit(items, null);
  return places;
};
var reparented = (before, after) => {
  const old = placesOf(before);
  const now = placesOf(after);
  return walk(after).flatMap((node) => {
    const previous = old.get(node.id);
    const place = now.get(node.id);
    return previous === void 0 || previous.parentId === place.parentId ? [] : [{ id: node.id, ...place, previousParentId: previous.parentId }];
  });
};
var keepsReadOnly = (before, after, isReadOnly) => {
  const locked = walk(before).filter(isReadOnly);
  if (locked.length === 0) return true;
  const old = placesOf(before);
  const now = placesOf(after);
  const items = new Map(walk(after).map((node) => [node.id, node]));
  const ids = (node) => node.children.map((child) => child.id).join("\n");
  return locked.every((node) => {
    const current = items.get(node.id);
    return current !== void 0 && current.text === node.text && current.checked === node.checked && now.get(node.id)?.parentId === old.get(node.id)?.parentId && ids(current) === ids(node);
  });
};
var refusal = (policy, before, after, move) => {
  if (after === before) return void 0;
  const { canMove, isReadOnly } = policy;
  if (move !== void 0) {
    const locked = isReadOnly !== void 0 && walk(before).some((node) => move.ids.includes(node.id) && isReadOnly(node));
    if (locked) return "Read-only items can't move.";
    if (canMove !== void 0 && !canMove({ ...move, before, after })) return "Can't move there.";
  }
  if (isReadOnly !== void 0 && !keepsReadOnly(before, after, isReadOnly)) {
    return "Read-only items can't change.";
  }
  return void 0;
};

// src/selectors.ts
var rowsOf = (model) => visibleRows(model.items, model.scopeId);
var selectedIds = (model, rows = rowsOf(model)) => {
  if (model.selection === null) return [];
  const anchor = rows.findIndex((row) => row.id === model.selection.anchorId);
  const head = rows.findIndex((row) => row.id === model.selection.headId);
  if (head < 0) return [];
  if (anchor < 0) return [model.selection.headId];
  return rows.slice(Math.min(anchor, head), Math.max(anchor, head) + 1).map((row) => row.id);
};
var selectedRoots = (model) => roots(model.items, selectedIds(model));

// src/text.ts
var BULLET = /^(?:[-*+•◦▪‣]|\d+[.)])\s+/;
var CHECKBOX = /^\[([ xX])\]\s+/;
var indentation = (line) => {
  let columns = 0;
  for (const character of line) {
    if (character === " ") columns += 1;
    else if (character === "	") columns += 4 - columns % 4;
    else break;
  }
  return columns;
};
var parseOutline = (text, nextId) => {
  const top = [];
  const stack = [];
  for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
    if (raw.trim() === "") continue;
    const columns = indentation(raw);
    let content = raw.trim().replace(BULLET, "");
    const task = CHECKBOX.exec(content);
    if (task !== null) content = content.slice(task[0].length);
    while (stack.length > 0 && stack[stack.length - 1].columns >= columns) stack.pop();
    const draft = { text: content, checked: task?.[1]?.toLowerCase() === "x", children: [] };
    (stack[stack.length - 1]?.children ?? top).push(draft);
    stack.push({ columns, children: draft.children });
  }
  const build = (drafts) => drafts.map(
    (draft) => item(nextId(), draft.text, build(draft.children), { checked: draft.checked })
  );
  return build(top);
};
var serializeOutline = (items, options = {}) => {
  const markdown = (options.format ?? "markdown") === "markdown";
  const lines = [];
  const visit = (nodes, depth) => nodes.forEach((node) => {
    const pad = markdown ? "  ".repeat(depth) : "	".repeat(depth);
    const marker = markdown ? `- ${node.checked ? "[x] " : ""}` : node.checked ? "[x] " : "";
    const [first = "", ...rest] = node.text.split("\n");
    lines.push(`${pad}${marker}${first}`);
    for (const line of rest) lines.push(`${pad}${markdown ? "  " : ""}${line}`);
    visit(node.children, depth + 1);
  });
  visit(items, 0);
  return lines.join("\n");
};

// src/mount.ts
var INDENT = 24;
var DRAG_THRESHOLD = 4;
var SCROLL_EDGE = 48;
var SETTLE_TIMEOUT = 200;
var HOVER_DELAY = 350;
var MODIFIER_KEYS = /* @__PURE__ */ new Set(["Shift", "Control", "Alt", "Meta", "CapsLock"]);
var EDITS = /* @__PURE__ */ new Set([
  "Pressed",
  "PastedText",
  "AcceptedCompletion",
  "Dropped"
]);
var FOCUSED_EVENT = "fw-outliner-focused";
var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
var MODEL_PROPERTY = "foldworksOutliner";
var POLICY_PROPERTY = "foldworksOutlinerPolicy";
var detectPlatform = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? "mac" : "other";
var rowIdOf = (target) => target instanceof Element ? target.closest("[data-outline-row]")?.dataset.outlineRow ?? void 0 : void 0;
var isText = (target) => target instanceof HTMLTextAreaElement && target.dataset.outlineText !== void 0;
var isPlaceholderText = (target) => target instanceof HTMLTextAreaElement && target.dataset.placeholderText !== void 0;
var ROW_OR_PLACEHOLDER = "[data-outline-row], [data-outline-placeholder]";
var neighbourOf = (row, delta) => {
  let at = delta > 0 ? row.nextElementSibling : row.previousElementSibling;
  while (at !== null && !at.matches(ROW_OR_PLACEHOLDER)) {
    at = delta > 0 ? at.nextElementSibling : at.previousElementSibling;
  }
  return at instanceof HTMLElement ? at : void 0;
};
var focusTextOf = (row, at) => {
  const text = row.querySelector("textarea");
  if (text === null) return;
  text.focus({ preventScroll: true });
  const offset = at === "Start" ? 0 : text.value.length;
  text.setSelectionRange(offset, offset);
  text.scrollIntoView({ block: "nearest", inline: "nearest" });
};
var scrollParent = (element) => {
  for (let node = element.parentElement; node !== null; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    if ((overflow === "auto" || overflow === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return void 0;
};
var sameTarget = (a, b) => JSON.stringify(a) === JSON.stringify(b);
var Surface = Mount.defineStream("OutlinerSurface", {
  messages: [
    Message.Pressed,
    Message.EditedText,
    Message.FocusedText,
    Message.PastedText,
    Message.ToggledCollapsed,
    Message.ClickedBullet,
    Message.SelectedRange,
    Message.StartedDrag,
    Message.MovedDrag,
    Message.Dropped,
    Message.CancelledDrag,
    Message.Hovered,
    Message.DismissedHover,
    Message.RequestedCompletion,
    Message.MovedCompletion,
    Message.AcceptedCompletion,
    Message.DismissedCompletion,
    Message.FilledPlaceholder
  ],
  execute: ({ element }) => Stream.callback(
    (queue) => Effect.gen(function* () {
      yield* Effect.acquireRelease(
        Effect.sync(() => {
          const host = element;
          const doc = host.ownerDocument;
          const platform = detectPlatform();
          let disposed = false;
          let drag;
          let press;
          const emit = (message) => {
            if (disposed) return;
            if (EDITS.has(message._tag)) unacknowledged.clear();
            Queue.offerUnsafe(queue, message);
          };
          let latest = host[MODEL_PROPERTY];
          let completionClosed = false;
          const model = () => latest;
          const unacknowledged = /* @__PURE__ */ new Map();
          let composing = false;
          const acknowledge = (next) => {
            for (const [id, typed] of unacknowledged) {
              const text = find(next.items, id)?.text;
              const at = text === void 0 ? -1 : typed.findIndex((entry) => entry.value === text);
              const ahead = at < 0 ? [] : typed.slice(at + 1);
              if (ahead.length === 0) {
                unacknowledged.delete(id);
                continue;
              }
              unacknowledged.set(id, ahead);
              const newest = ahead[ahead.length - 1];
              queueMicrotask(() => {
                const element2 = doc.getElementById(domIds(next.id).text(id));
                if (!(element2 instanceof HTMLTextAreaElement) || composing) return;
                if (element2.value === newest.value) return;
                element2.value = newest.value;
                if (doc.activeElement === element2) {
                  element2.setSelectionRange(newest.start, newest.end);
                }
              });
            }
          };
          Object.defineProperty(host, MODEL_PROPERTY, {
            configurable: true,
            get: () => latest,
            set: (next) => {
              latest = next;
              completionClosed = false;
              acknowledge(next);
            }
          });
          let held = [];
          let settling = false;
          let settleTimer;
          const awaitFocus = () => {
            settling = true;
            clearTimeout(settleTimer);
            settleTimer = setTimeout(release, SETTLE_TIMEOUT);
          };
          const pressAction = (action, id, start = 0, end = 0, goalX = 0) => {
            emit(Message.Pressed({ action, id, start, end, goalX }));
            if (!keepsFocus(action)) awaitFocus();
          };
          const completionKey = (event, target, id) => {
            const list = model()?.completion;
            if (completionClosed || list === null || list === void 0 || list.id !== id) {
              return false;
            }
            if (doc.getElementById(domIds(model().id).completion) === null) return false;
            const shown = Completion.visible(list, target.value, target.selectionEnd);
            const plain = !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey;
            if (shown.length === 0 || !plain) return false;
            switch (event.key) {
              case "ArrowDown":
              case "ArrowUp":
                emit(Message.MovedCompletion({ delta: event.key === "ArrowDown" ? 1 : -1 }));
                return true;
              case "Enter":
              case "Tab":
                emit(Message.AcceptedCompletion({}));
                completionClosed = true;
                awaitFocus();
                return true;
              case "Escape":
                emit(Message.DismissedCompletion());
                completionClosed = true;
                return true;
              case "ArrowLeft":
              case "ArrowRight":
              case "Home":
              case "End":
              case "PageUp":
              case "PageDown":
                emit(Message.DismissedCompletion());
                completionClosed = true;
                return false;
              default:
                return false;
            }
          };
          const fill = (row, typed, typedCaret) => {
            const template = row.dataset.text ?? "";
            const caret = Number(row.dataset.caret ?? template.length);
            emit(
              Message.FilledPlaceholder({
                parentId: row.dataset.parent ?? null,
                index: Number(row.dataset.index ?? 0),
                key: row.dataset.outlinePlaceholder ?? "",
                text: template.slice(0, caret) + typed + template.slice(caret),
                offset: caret + typedCaret
              })
            );
            awaitFocus();
          };
          const placeholderKey = (event, target) => {
            const row = target.closest("[data-outline-placeholder]");
            if (row === null) return false;
            const plain = !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey;
            if (event.key === "Tab") return true;
            if (!plain) return false;
            switch (event.key) {
              case "Enter":
                fill(row, "", 0);
                return true;
              case "ArrowUp":
              case "ArrowLeft":
              case "Backspace":
              case "Escape": {
                const above = neighbourOf(row, -1);
                if (above !== void 0) focusTextOf(above, "End");
                return true;
              }
              case "ArrowDown":
              case "ArrowRight": {
                const below = neighbourOf(row, 1);
                if (below !== void 0) focusTextOf(below, "Start");
                return true;
              }
              default:
                return false;
            }
          };
          const intoPlaceholder = (action, target) => {
            const row = target.closest("[data-outline-row]");
            const delta = action === "FocusNext" || action === "FocusNextStart" ? 1 : action === "FocusPrevious" || action === "FocusPreviousEnd" ? -1 : 0;
            if (row === null || delta === 0) return false;
            const view2 = (delta > 0 ? row : neighbourOf(row, -1))?.querySelector(
              ":scope > [data-outline-view]"
            );
            if (view2 !== null && view2 !== void 0) {
              view2.focus();
              return true;
            }
            const neighbour2 = neighbourOf(row, delta);
            if (neighbour2?.dataset.outlinePlaceholder === void 0) return false;
            focusTextOf(neighbour2, delta > 0 ? "Start" : "End");
            return true;
          };
          const viewKey = (event, view2) => {
            const plain = !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey;
            const row = view2.closest("[data-outline-row]");
            if (!plain || row === null || event.defaultPrevented) return;
            if (event.key === "Escape") {
              event.preventDefault();
              focusTextOf(row, "End");
              return;
            }
            if (event.target !== view2) return;
            if (event.key === "ArrowUp") {
              event.preventDefault();
              focusTextOf(row, "End");
            } else if (event.key === "ArrowDown") {
              event.preventDefault();
              const below = neighbourOf(row, 1);
              if (below !== void 0) focusTextOf(below, "Start");
            }
          };
          const handleKey = (event, target, replayed = false) => {
            if (isPlaceholderText(target)) return placeholderKey(event, target);
            if (isText(target)) {
              const id2 = rowIdOf(target);
              if (id2 === void 0) return false;
              if (!replayed && completionKey(event, target, id2)) return true;
              const vertical = event.key === "ArrowUp" || event.key === "ArrowDown";
              const lines = vertical ? caretLines(target) : { onFirstLine: false, onLastLine: false, x: 0 };
              const action2 = resolveKey(
                event,
                {
                  mode: "Text",
                  caret: {
                    start: target.selectionStart,
                    end: target.selectionEnd,
                    length: target.value.length,
                    onFirstLine: lines.onFirstLine,
                    onLastLine: lines.onLastLine
                  }
                },
                platform
              );
              if (action2 === void 0) return false;
              if (intoPlaceholder(action2, target)) return true;
              if (action2 === "Complete") {
                emit(
                  Message.RequestedCompletion({
                    id: id2,
                    start: target.selectionStart,
                    end: target.selectionEnd
                  })
                );
                return true;
              }
              pressAction(action2, id2, target.selectionStart, target.selectionEnd, lines.x);
              return true;
            }
            const id = target instanceof HTMLElement ? target.dataset.outlineRow : void 0;
            if (id === void 0) return false;
            const action = resolveKey(event, { mode: "Rows" }, platform);
            if (action === void 0) return false;
            pressAction(action, id);
            return true;
          };
          const replay = (event) => {
            const target = doc.activeElement;
            if (target === null || !host.contains(target) || handleKey(event, target, true))
              return;
            const writable = isText(target) && !target.readOnly || isPlaceholderText(target);
            if (!writable || event.ctrlKey || event.metaKey) return;
            const text = event.key === "Enter" ? "\n" : event.key.length === 1 ? event.key : "";
            const command = text !== "" ? "insertText" : event.key === "Backspace" ? "delete" : event.key === "Delete" ? "forwardDelete" : void 0;
            if (command !== void 0 && !doc.execCommand(command, false, text)) {
              if (text === "") return;
              target.setRangeText(text, target.selectionStart, target.selectionEnd, "end");
              target.dispatchEvent(new InputEvent("input", { bubbles: true, data: text }));
            }
          };
          function release() {
            clearTimeout(settleTimer);
            settling = false;
            while (held.length > 0 && !settling) replay(held.shift());
          }
          let hoverTimer;
          let hoverPoint;
          const hoverPopup = () => doc.getElementById(domIds(model()?.id ?? "").hover);
          const hoveredRects = () => [...host.querySelectorAll(".fw-text-hovered")].flatMap((element2) => [
            ...element2.getClientRects()
          ]);
          const emitHover = (target) => {
            const current = model()?.hover ?? null;
            const unchanged = target === null ? (
              // The pointer only dismisses what it asked for, not information shown for the caret.
              current === null || current.source === "Keyboard"
            ) : current?.source === "Pointer" && current.id === target.id && current.offset === target.offset;
            if (!unchanged) emit(Message.Hovered({ target }));
          };
          const settleHover = () => {
            const point = hoverPoint;
            const under = point === void 0 ? null : doc.elementFromPoint(point.x, point.y);
            const id = isText(under) ? rowIdOf(under) : void 0;
            const mirror = under?.parentElement?.querySelector("[data-outline-mirror]");
            const offset = point === void 0 || mirror === null || mirror === void 0 ? void 0 : offsetAtPoint(mirror, point.x, point.y);
            emitHover(id === void 0 || offset === void 0 ? null : { id, offset });
          };
          const trackHover = (event) => {
            if (event.pointerType === "touch") return;
            const target = event.target instanceof Element ? event.target : null;
            if (target?.closest("[data-text-popup]") || isOver(hoveredRects(), event.clientX, event.clientY)) {
              clearTimeout(hoverTimer);
              return;
            }
            hoverPoint = { x: event.clientX, y: event.clientY };
            clearTimeout(hoverTimer);
            hoverTimer = setTimeout(settleHover, HOVER_DELAY);
          };
          const leave = () => {
            hoverPoint = void 0;
            clearTimeout(hoverTimer);
            hoverTimer = setTimeout(settleHover, HOVER_DELAY);
          };
          const keydown = (event) => {
            if (drag?.active && event.key === "Escape") {
              event.preventDefault();
              endDrag(false);
              return;
            }
            const view2 = event.target instanceof Element ? event.target.closest("[data-outline-view]") : null;
            if (view2 !== null) {
              viewKey(event, view2);
              return;
            }
            if (hoverPopup() !== null && !MODIFIER_KEYS.has(event.key)) {
              clearTimeout(hoverTimer);
              const plain = !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey;
              if (event.key === "Escape" && plain) {
                event.preventDefault();
                emit(Message.DismissedHover());
                return;
              }
              if (model()?.hover?.source === "Keyboard") emit(Message.DismissedHover());
            }
            if (event.defaultPrevented || event.isComposing || event.keyCode === 229) return;
            if (settling) {
              if (MODIFIER_KEYS.has(event.key)) return;
              event.preventDefault();
              held.push({
                key: event.key,
                code: event.code,
                shiftKey: event.shiftKey,
                altKey: event.altKey,
                ctrlKey: event.ctrlKey,
                metaKey: event.metaKey
              });
              return;
            }
            const target = event.target;
            if (handleKey(event, target)) {
              event.preventDefault();
              return;
            }
            if (target === doc.getElementById(domIds(model()?.id ?? "").tree)) {
              if (event.key === "Tab" && !event.shiftKey) {
                const next = [...doc.querySelectorAll(FOCUSABLE)].find(
                  (candidate) => !host.contains(candidate) && (host.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
                );
                if (next !== void 0) {
                  event.preventDefault();
                  next.focus();
                }
                return;
              }
              const first = host.querySelector("[data-outline-row]")?.dataset.outlineRow;
              if (first !== void 0 && (event.key === "ArrowDown" || event.key === "Enter")) {
                event.preventDefault();
                emit(Message.SelectedRange({ anchorId: first, headId: first }));
              }
            }
          };
          const fillFromInput = (target) => {
            const row = target.closest("[data-outline-placeholder]");
            const typed = target.value;
            if (row === null || typed === "") return;
            const caret = target.selectionEnd;
            target.value = "";
            fill(row, typed, caret);
          };
          const input = (event) => {
            const target = event.target;
            if (isPlaceholderText(target)) {
              if (!event.isComposing) fillFromInput(target);
              return;
            }
            if (!isText(target) || composing || event.isComposing) return;
            const id = rowIdOf(target);
            if (id === void 0) return;
            const typed = unacknowledged.get(id) ?? [];
            typed.push({
              value: target.value,
              start: target.selectionStart,
              end: target.selectionEnd
            });
            unacknowledged.set(id, typed.slice(-64));
            emit(
              Message.EditedText({
                id,
                text: target.value,
                start: target.selectionStart,
                end: target.selectionEnd,
                time: event.timeStamp
              })
            );
          };
          const beforeinput = (event) => {
            if (event.inputType === "historyUndo" || event.inputType === "historyRedo") {
              const target = event.target;
              const id = rowIdOf(target);
              if (!isText(target) || id === void 0) return;
              event.preventDefault();
              pressAction(
                event.inputType === "historyUndo" ? "Undo" : "Redo",
                id,
                target.selectionStart,
                target.selectionEnd
              );
            }
          };
          const focusin = (event) => {
            const target = event.target;
            if (!isText(target)) return;
            const id = rowIdOf(target);
            if (id === void 0) return;
            emit(
              Message.FocusedText({ id, start: target.selectionStart, end: target.selectionEnd })
            );
          };
          const selectionText = () => {
            const current = model();
            if (current === void 0 || current.mode !== "Rows") return void 0;
            const nodes = selectedRoots(current).map((id) => find(current.items, id)).filter((node) => node !== void 0);
            return nodes.length === 0 ? void 0 : serializeOutline(nodes);
          };
          const inView = (target) => target instanceof Element && target.closest("[data-outline-view]") !== null;
          const copy = (event) => {
            if (isText(event.target) || inView(event.target)) return;
            const text = selectionText();
            if (text === void 0 || event.clipboardData === null) return;
            event.preventDefault();
            event.clipboardData.setData("text/plain", text);
            const id = model()?.selection?.headId;
            if (event.type === "cut" && id !== void 0) pressAction("Delete", id);
          };
          const compositionstart = () => {
            composing = true;
            if (model()?.completion) emit(Message.DismissedCompletion());
            if (model()?.hover) emit(Message.DismissedHover());
          };
          const compositionend = (event) => {
            composing = false;
            if (isPlaceholderText(event.target)) fillFromInput(event.target);
            else if (isText(event.target)) input(event);
          };
          const paste = (event) => {
            const text = event.clipboardData?.getData("text/plain") ?? "";
            if (text === "") return;
            const target = event.target;
            if (isPlaceholderText(target) || inView(target)) return;
            if (isText(target)) {
              if (!/\r|\n/.test(text.replace(/[\r\n]+$/, ""))) return;
              const id2 = rowIdOf(target);
              if (id2 === void 0) return;
              event.preventDefault();
              awaitFocus();
              emit(
                Message.PastedText({
                  id: id2,
                  mode: "Text",
                  start: target.selectionStart,
                  end: target.selectionEnd,
                  text
                })
              );
              return;
            }
            const id = model()?.selection?.headId;
            if (id === void 0) return;
            event.preventDefault();
            emit(Message.PastedText({ id, mode: "Rows", start: 0, end: 0, text }));
          };
          const rowAt = (x, y) => rowIdOf(doc.elementFromPoint(x, y));
          const computeTarget = (current) => {
            const state = model();
            if (state === void 0) return null;
            const rows = visibleRows(state.items, state.scopeId);
            const dragged = new Set(current.ids);
            const parents = new Map(rows.map((row) => [row.id, row.parentId]));
            const hidden = (id) => {
              for (let at = id; at !== null; at = parents.get(at) ?? null) {
                if (dragged.has(at)) return true;
              }
              return false;
            };
            let gap = 0;
            for (const row of rows) {
              if (hidden(row.id)) continue;
              const rect = doc.getElementById(domIds(state.id).row(row.id))?.getBoundingClientRect();
              if (rect === void 0 || rect.top + rect.height / 2 >= current.y) break;
              gap += 1;
            }
            const depth = current.depth + (current.x - current.startX) / INDENT;
            const wanted = dropTarget(rows, current.ids, gap, depth, state.scopeId);
            const policy = host[POLICY_PROPERTY] ?? {};
            if (policy.canMove === void 0 && policy.isReadOnly === void 0) return wanted;
            const allowed = (target) => {
              const after = moveItems(state.items, current.ids, target.placement);
              return after === void 0 || refusal(policy, state.items, after, { cause: "Drop", ids: current.ids }) === void 0;
            };
            if (allowed(wanted)) return wanted;
            const deepest = Math.max(0, ...rows.map((row) => row.depth)) + 1;
            const depths = Array.from({ length: deepest + 1 }, (_, level) => level).sort(
              (a, b) => Math.abs(a - wanted.depth) - Math.abs(b - wanted.depth)
            );
            for (const level of depths) {
              const candidate = dropTarget(rows, current.ids, gap, level, state.scopeId);
              if (allowed(candidate)) return candidate;
            }
            return { ...wanted, refused: true };
          };
          const placeGhost = (current) => {
            if (current.ghost !== null) {
              current.ghost.style.transform = `translate(${current.x + 14}px, ${current.y + 10}px)`;
            }
          };
          const retarget = (current) => {
            const target = computeTarget(current);
            if (!sameTarget(target, current.target)) {
              current.target = target;
              if (current.ghost !== null) {
                current.ghost.dataset.refused = String(target?.refused === true);
              }
              emit(Message.MovedDrag({ target }));
            }
          };
          const autoscroll = () => {
            const current = drag;
            if (current === void 0 || !current.active || disposed) return;
            const scroller = scrollParent(host);
            const top = scroller?.getBoundingClientRect().top ?? 0;
            const bottom = scroller?.getBoundingClientRect().bottom ?? window.innerHeight;
            const delta = current.y < top + SCROLL_EDGE ? -Math.ceil((top + SCROLL_EDGE - current.y) / 4) : current.y > bottom - SCROLL_EDGE ? Math.ceil((current.y - (bottom - SCROLL_EDGE)) / 4) : 0;
            if (delta !== 0) {
              if (scroller === void 0) window.scrollBy(0, delta);
              else scroller.scrollTop += delta;
              retarget(current);
            }
            current.frame = requestAnimationFrame(autoscroll);
          };
          const startDrag = (current) => {
            const state = model();
            if (state === void 0) return;
            const selected = state.mode === "Rows" ? selectedIds(state) : [];
            current.ids = selected.includes(current.rowId) ? roots(state.items, selected) : [current.rowId];
            current.depth = visibleRows(state.items, state.scopeId).find((row) => row.id === current.ids[0])?.depth ?? 0;
            const isReadOnly = host[POLICY_PROPERTY]?.isReadOnly;
            if (isReadOnly !== void 0 && current.ids.some((id) => {
              const node = find(state.items, id);
              return node !== void 0 && isReadOnly(node);
            })) {
              drag = void 0;
              return;
            }
            current.active = true;
            const ghost = doc.createElement("div");
            ghost.className = "fw-outliner-ghost";
            const first = find(state.items, current.ids[0])?.text.split("\n")[0] || "Untitled";
            ghost.textContent = current.ids.length > 1 ? `${first}  +${current.ids.length - 1}` : first;
            doc.body.append(ghost);
            current.ghost = ghost;
            doc.documentElement.classList.add("fw-outliner-dragging");
            placeGhost(current);
            emit(Message.StartedDrag({ ids: current.ids }));
            retarget(current);
            current.frame = requestAnimationFrame(autoscroll);
          };
          const endDrag = (dropped) => {
            const current = drag;
            drag = void 0;
            if (current === void 0) return;
            cancelAnimationFrame(current.frame);
            current.ghost?.remove();
            doc.documentElement.classList.remove("fw-outliner-dragging");
            if (current.active) emit(dropped ? Message.Dropped() : Message.CancelledDrag());
          };
          const pointerdown = (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (target === null || event.button !== 0) return;
            if (model()?.completion && !target.closest("[data-text-popup]")) {
              emit(Message.DismissedCompletion());
            }
            if (inView(target)) return;
            if (target.closest("[data-outline-control]")) {
              event.preventDefault();
              return;
            }
            const handle = target.closest("[data-outline-handle]");
            const id = rowIdOf(target);
            if (handle !== null && id !== void 0) {
              event.preventDefault();
              handle.setPointerCapture(event.pointerId);
              drag = {
                pointerId: event.pointerId,
                rowId: id,
                startX: event.clientX,
                startY: event.clientY,
                x: event.clientX,
                y: event.clientY,
                active: false,
                ids: [id],
                depth: 0,
                target: null,
                ghost: null,
                frame: 0
              };
              return;
            }
            if (id === void 0) return;
            const state = model();
            const activeRow = rowIdOf(doc.activeElement);
            const anchorId = state?.mode === "Rows" && state.selection !== null ? state.selection.anchorId : host.contains(doc.activeElement) ? activeRow : void 0;
            if (event.shiftKey && anchorId !== void 0 && anchorId !== id) {
              event.preventDefault();
              emit(Message.SelectedRange({ anchorId, headId: id }));
              return;
            }
            press = { rowId: id, headId: id };
          };
          const pointermove = (event) => {
            const current = drag;
            if (current !== void 0 && current.pointerId === event.pointerId) {
              current.x = event.clientX;
              current.y = event.clientY;
              if (!current.active) {
                const distance = Math.hypot(
                  current.x - current.startX,
                  current.y - current.startY
                );
                if (distance < DRAG_THRESHOLD) return;
                startDrag(current);
                return;
              }
              placeGhost(current);
              retarget(current);
              return;
            }
            if (event.buttons === 0) trackHover(event);
            if (press === void 0 || (event.buttons & 1) === 0) return;
            const over = rowAt(event.clientX, event.clientY);
            if (over === void 0 || over === press.headId) return;
            press.headId = over;
            if (over !== press.rowId || model()?.mode === "Rows") {
              doc.getSelection()?.removeAllRanges();
              emit(Message.SelectedRange({ anchorId: press.rowId, headId: over }));
            }
          };
          const pointerup = (event) => {
            press = void 0;
            const current = drag;
            if (current === void 0 || current.pointerId !== event.pointerId) return;
            if (current.active) {
              current.x = event.clientX;
              current.y = event.clientY;
              retarget(current);
              endDrag(true);
            } else {
              drag = void 0;
              emit(Message.ClickedBullet({ id: current.rowId }));
            }
          };
          const pointercancel = () => {
            press = void 0;
            endDrag(false);
          };
          const click = (event) => {
            const toggle = event.target instanceof Element ? event.target.closest("[data-outline-toggle]") : null;
            const id = toggle?.dataset.outlineToggle;
            if (id === void 0) return;
            event.preventDefault();
            emit(Message.ToggledCollapsed({ id, recursive: event.altKey }));
          };
          const documentKeydown = (event) => {
            if (drag?.active && event.key === "Escape" && !host.contains(event.target)) {
              event.preventDefault();
              endDrag(false);
            }
          };
          const listeners = [
            [host, "keydown", keydown],
            [host, "input", input],
            [host, "compositionstart", compositionstart],
            [host, "compositionend", compositionend],
            [host, "beforeinput", beforeinput],
            [host, "focusin", focusin],
            [host, "copy", copy],
            [host, "cut", copy],
            [host, "paste", paste],
            [host, "pointerdown", pointerdown],
            [host, "pointermove", pointermove],
            [host, "pointercancel", pointercancel],
            [host, "pointerleave", leave],
            [host, "click", click],
            [host, FOCUSED_EVENT, release],
            [doc, "pointerup", pointerup],
            [doc, "keydown", documentKeydown]
          ];
          for (const [target, type, listener] of listeners)
            target.addEventListener(type, listener);
          return () => {
            disposed = true;
            Object.defineProperty(host, MODEL_PROPERTY, {
              configurable: true,
              writable: true,
              value: latest
            });
            clearTimeout(settleTimer);
            clearTimeout(hoverTimer);
            endDrag(false);
            for (const [target, type, listener] of listeners) {
              target.removeEventListener(type, listener);
            }
          };
        }),
        (dispose) => Effect.sync(dispose)
      );
      return yield* Effect.never;
    })
  )
});

// src/update.ts
var HISTORY_LIMIT = 200;
var TYPING_PAUSE = 1200;
var FocusRequest = Schema.Union([
  Schema.TaggedStruct("Text", { id: Schema.String, start: Schema.Number, end: Schema.Number }),
  Schema.TaggedStruct("TextAtX", { id: Schema.String, x: Schema.Number, line: Schema.Literals(["First", "Last"]) }),
  Schema.TaggedStruct("Row", { id: Schema.String }),
  Schema.TaggedStruct("Tree", {}),
  Schema.TaggedStruct("Add", {})
]);
var FocusOutliner = Command.define("FocusOutliner", {
  args: { modelId: Schema.String, request: FocusRequest },
  messages: [Message.CompletedFocus],
  execute: ({ modelId, request }) => Effect.gen(function* () {
    yield* afterCommit;
    const ids = domIds(modelId);
    const elementId = request._tag === "Text" || request._tag === "TextAtX" ? ids.text(request.id) : request._tag === "Row" ? ids.row(request.id) : request._tag === "Add" ? ids.add : ids.tree;
    const element = document.getElementById(elementId);
    if (element === null) return Message.CompletedFocus();
    if (document.activeElement !== element) element.focus({ preventScroll: true });
    if (element instanceof HTMLTextAreaElement) {
      if (request._tag === "Text") {
        const length = element.value.length;
        element.setSelectionRange(Math.min(request.start, length), Math.min(request.end, length));
      } else if (request._tag === "TextAtX") {
        const offset = offsetAtX(element, request.x, request.line);
        element.setSelectionRange(offset, offset);
      }
    }
    element.scrollIntoView({ block: "nearest", inline: "nearest" });
    element.dispatchEvent(new CustomEvent(FOCUSED_EVENT, { bubbles: true }));
    return Message.CompletedFocus();
  })
});
var focusCommand = (model, request) => FocusOutliner({ modelId: model.id, request });
var newId = (model, items = model.items) => {
  let counter = model.nextId;
  while (find(items, `${model.id}-${counter}`) !== void 0) counter += 1;
  return { id: `${model.id}-${counter}`, nextId: counter + 1 };
};
var commit = (model, items, options = {}) => ({
  ...model,
  items,
  history: History.record(
    model.history,
    { items: model.items, focus: options.before === void 0 ? model.focus : options.before },
    {
      limit: HISTORY_LIMIT,
      ...options.coalescingKey === void 0 ? {} : { coalescingKey: options.coalescingKey }
    }
  ),
  revision: model.revision + 1,
  announcement: options.announcement ?? model.announcement
});
var editText = (model, focus, items, extra = {}) => ({
  model: { ...model, ...extra, items, mode: "Text", selection: null, focus },
  commands: [
    focusCommand(model, { _tag: "Text", id: focus.id, start: focus.start, end: focus.end })
  ]
});
var textFocus = (id, offset) => ({ id, start: offset, end: offset });
var selectRows = (model, anchorId, headId) => ({
  model: { ...model, mode: "Rows", selection: { anchorId, headId } },
  commands: [focusCommand(model, { _tag: "Row", id: headId })]
});
var refocus = (model) => {
  if (model.mode === "Rows" && model.selection !== null) {
    return { model, commands: [focusCommand(model, { _tag: "Row", id: model.selection.headId })] };
  }
  return model.focus === null ? { model } : {
    model,
    commands: [
      focusCommand(model, {
        _tag: "Text",
        id: model.focus.id,
        start: model.focus.start,
        end: model.focus.end
      })
    ]
  };
};
var neighbour = (rows, id, delta) => {
  const index = rows.findIndex((row) => row.id === id);
  return index < 0 ? void 0 : rows[index + delta];
};
var plural = (count, noun) => count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
var keepCollapse = (restored, current) => {
  const collapsed = new Map(walk(current).map((node) => [node.id, node.collapsed]));
  const apply = (nodes) => nodes.map((node) => {
    const state = collapsed.get(node.id);
    const children = apply(node.children);
    return {
      ...node,
      collapsed: node.children.length > 0 && (state ?? node.collapsed),
      children
    };
  });
  return apply(restored);
};
var foldScope = (model, fold) => model.scopeId === null ? fold(model.items) : updateItem(model.items, model.scopeId, (node) => ({
  ...node,
  children: fold(node.children)
}));
var refold = (model, items, announcement) => {
  const next = {
    ...model,
    items,
    revision: model.revision + 1,
    mode: "Text",
    selection: null,
    announcement
  };
  if (model.focus === null) return next;
  const visible = new Set(rowsOf(next).map((row) => row.id));
  if (visible.has(model.focus.id)) return next;
  const ancestor = ancestors(items, model.focus.id).toReversed().find((id) => visible.has(id));
  return {
    ...next,
    focus: ancestor === void 0 ? null : textFocus(ancestor, find(items, ancestor)?.text.length ?? 0)
  };
};
var travel = (model, direction, current) => {
  const snapshot = { items: model.items, focus: current };
  const restored = direction === "Undo" ? History.undo(model.history, snapshot) : History.redo(model.history, snapshot);
  if (restored === void 0) return { model };
  const items = keepCollapse(restored.value.items, model.items);
  const focus = restored.value.focus;
  const scopeId = model.scopeId !== null && find(items, model.scopeId) === void 0 ? null : model.scopeId;
  const next = {
    ...model,
    items,
    scopeId,
    history: restored.history,
    revision: model.revision + 1,
    announcement: direction === "Undo" ? "Undone." : "Redone."
  };
  if (focus !== null && find(items, focus.id) !== void 0) {
    const revealed = ancestors(items, focus.id).reduce(
      (nodes, id) => setCollapsed(nodes, id, false),
      items
    );
    return editText(model, focus, revealed, {
      scopeId: next.scopeId,
      history: next.history,
      revision: next.revision,
      announcement: next.announcement
    });
  }
  return { model: { ...next, mode: "Text", selection: null, focus: null } };
};
var toggleChecked = (model, ids) => {
  const nodes = ids.map((id) => find(model.items, id)).filter((node) => node !== void 0);
  if (nodes.length === 0) return model;
  const checked = !nodes.every((node) => node.checked);
  const items = nodes.reduce(
    (current, node) => updateItem(current, node.id, (value) => ({ ...value, checked })),
    model.items
  );
  return commit(model, items, {
    announcement: `${plural(nodes.length, "item")} marked ${checked ? "done" : "not done"}.`
  });
};
var hoist = (model, scopeId, focusId) => {
  if (scopeId !== null && find(model.items, scopeId) === void 0) return { model };
  const items = scopeId === null ? model.items : setCollapsed(model.items, scopeId, false);
  const next = {
    ...model,
    items,
    scopeId,
    selection: null,
    mode: "Text",
    announcement: scopeId === null ? "Showing the whole outline." : `Hoisted ${find(items, scopeId)?.text || "untitled item"}.`
  };
  const rows = rowsOf(next);
  const target = focusId ?? rows[0]?.id;
  if (target === void 0) {
    return { model: { ...next, focus: null }, commands: [focusCommand(next, { _tag: "Add" })] };
  }
  const length = find(items, target)?.text.length ?? 0;
  return editText(next, textFocus(target, length), items);
};
var deleteRows = (model, ids, mode) => {
  const rows = rowsOf(model);
  const removing = roots(model.items, ids);
  if (removing.length === 0) return { model };
  const removedSet = new Set(removing);
  const hidden = (row) => removedSet.has(row.id) || ancestors(model.items, row.id).some((id) => removedSet.has(id));
  const firstIndex = rows.findIndex((row) => removedSet.has(row.id));
  const after = rows.slice(firstIndex + 1).find((row) => !hidden(row));
  const before = rows.slice(0, Math.max(0, firstIndex)).reverse().find((row) => !hidden(row));
  const target = after ?? before;
  const items = removeItems(model.items, removing).items;
  const committed = commit(model, items, {
    announcement: `Deleted ${plural(removing.length, "item")}.`
  });
  if (target === void 0) {
    return {
      model: { ...committed, mode: "Text", selection: null, focus: null },
      commands: [focusCommand(model, { _tag: "Add" })]
    };
  }
  return mode === "Rows" ? selectRows({ ...committed, focus: null }, target.id, target.id) : editText(
    committed,
    textFocus(target.id, after === void 0 ? target.text.length : 0),
    items
  );
};
var pressed = (model, action, id, start, end, goalX) => {
  const rows = rowsOf(model);
  const node = find(model.items, id);
  if (node === void 0) return { model };
  const caret = { id, start, end };
  const inRows = model.mode === "Rows" && model.selection !== null;
  const targets = inRows ? selectedRoots(model) : [id];
  const base = inRows ? model : { ...model, focus: caret, mode: "Text" };
  switch (action) {
    case "Split": {
      const location = locate(model.items, id);
      const siblings = childrenOf(model.items, location.parentId) ?? [];
      const isLastEmptyChild = node.text === "" && node.children.length === 0 && location.parentId !== null && location.parentId !== model.scopeId && location.index === siblings.length - 1;
      if (isLastEmptyChild) {
        const items = outdent(model.items, [id], model.scopeId);
        if (items !== void 0) {
          return editText(
            commit(base, items, { announcement: "Outdented." }),
            textFocus(id, 0),
            items
          );
        }
      }
      const { id: created, nextId } = newId(model);
      const result = split(model.items, id, start, end, created);
      if (result === void 0) return { model };
      return editText(
        { ...commit(base, result.items), nextId },
        textFocus(result.focusId, result.offset),
        result.items
      );
    }
    case "Indent":
    case "Outdent": {
      const items = action === "Indent" ? indent(model.items, targets) : outdent(model.items, targets, model.scopeId);
      if (items === void 0) return { model: base };
      const announcement = `${action === "Indent" ? "Indented" : "Outdented"} ${plural(targets.length, "item")}.`;
      return refocus(commit(base, items, { announcement }));
    }
    case "MoveUp":
    case "MoveDown": {
      const items = action === "MoveUp" ? moveUp(model.items, targets, model.scopeId) : moveDown(model.items, targets, model.scopeId);
      if (items === void 0) return { model: base };
      return refocus(
        commit(base, items, { announcement: `Moved ${action === "MoveUp" ? "up" : "down"}.` })
      );
    }
    case "Collapse":
    case "Expand": {
      const collapsed = action === "Collapse";
      const items = targets.reduce(
        (current, target) => setCollapsed(current, target, collapsed),
        model.items
      );
      return items === model.items ? { model: base } : refocus({
        ...base,
        items,
        revision: model.revision + 1,
        announcement: collapsed ? "Collapsed." : "Expanded."
      });
    }
    case "ToggleChecked":
      return { model: toggleChecked(base, targets) };
    case "ZoomIn":
      return hoist(base, inRows ? model.selection?.headId ?? id : id);
    case "ZoomOut": {
      if (model.scopeId === null) return { model: base };
      const parent = locate(model.items, model.scopeId)?.parentId ?? null;
      return hoist(base, parent, model.scopeId);
    }
    case "Undo":
    case "Redo":
      return travel(model, action, inRows ? model.focus : caret);
    case "MergePrevious": {
      if (node.text === "" && node.children.length === 0 && rows[0]?.id === id) {
        const next = neighbour(rows, id, 1);
        return next === void 0 ? { model: base } : deleteRows(base, [id], "Text");
      }
      const result = mergeIntoPrevious(model.items, id, model.scopeId);
      if (result === void 0) return { model: base };
      return editText(
        commit(base, result.items),
        textFocus(result.focusId, result.offset),
        result.items
      );
    }
    case "MergeNext": {
      const result = mergeNext(model.items, id, model.scopeId);
      if (result === void 0) return { model: base };
      return editText(commit(base, result.items), textFocus(id, result.offset), result.items);
    }
    case "Delete":
      return deleteRows(base, targets, inRows ? "Rows" : "Text");
    case "FocusPrevious":
    case "FocusNext": {
      const target = neighbour(rows, id, action === "FocusPrevious" ? -1 : 1);
      if (target === void 0) {
        const offset = action === "FocusPrevious" ? 0 : node.text.length;
        return editText(base, textFocus(id, offset), model.items);
      }
      return {
        model: { ...base, focus: textFocus(target.id, 0) },
        commands: [
          focusCommand(model, {
            _tag: "TextAtX",
            id: target.id,
            x: goalX,
            line: action === "FocusPrevious" ? "Last" : "First"
          })
        ]
      };
    }
    case "FocusPreviousEnd": {
      const target = neighbour(rows, id, -1);
      return target === void 0 ? { model: base } : editText(base, textFocus(target.id, target.text.length), model.items);
    }
    case "FocusNextStart": {
      const target = neighbour(rows, id, 1);
      return target === void 0 ? { model: base } : editText(base, textFocus(target.id, 0), model.items);
    }
    case "SelectRow":
      return selectRows(base, id, id);
    case "ExtendUp":
    case "ExtendDown": {
      const anchorId = inRows ? model.selection.anchorId : id;
      const headId = inRows ? model.selection.headId : id;
      const target = neighbour(rows, headId, action === "ExtendUp" ? -1 : 1);
      return selectRows(base, anchorId, target?.id ?? headId);
    }
    case "SelectPrevious":
    case "SelectNext": {
      const headId = model.selection?.headId ?? id;
      const target = neighbour(rows, headId, action === "SelectPrevious" ? -1 : 1) ?? find(model.items, headId);
      return target === void 0 ? { model: base } : selectRows(base, target.id, target.id);
    }
    case "CollapseOrParent": {
      const headId = model.selection?.headId ?? id;
      const head = rows.find((row) => row.id === headId);
      if (head === void 0) return { model: base };
      if (head.hasChildren && !head.collapsed) {
        return {
          model: {
            ...base,
            items: setCollapsed(model.items, headId, true),
            revision: model.revision + 1
          }
        };
      }
      return head.parentId === null || head.parentId === model.scopeId ? { model: base } : selectRows(base, head.parentId, head.parentId);
    }
    case "ExpandOrChild": {
      const headId = model.selection?.headId ?? id;
      const head = rows.find((row) => row.id === headId);
      if (head === void 0 || !head.hasChildren) return { model: base };
      if (head.collapsed) {
        return {
          model: {
            ...base,
            items: setCollapsed(model.items, headId, false),
            revision: model.revision + 1
          }
        };
      }
      const child = neighbour(rows, headId, 1);
      return child === void 0 ? { model: base } : selectRows(base, child.id, child.id);
    }
    case "Edit": {
      const headId = model.selection?.headId ?? id;
      const text = find(model.items, headId)?.text ?? "";
      const focus = model.focus !== null && model.focus.id === headId ? model.focus : textFocus(headId, text.length);
      return editText(base, focus, model.items);
    }
    case "ClearSelection":
      return {
        model: { ...base, mode: "Text", selection: null },
        commands: [focusCommand(model, { _tag: "Tree" })]
      };
    case "ShowInfo":
      return { model: { ...base, hover: { id, offset: start, source: "Keyboard" } } };
    case "Complete":
      return { model: base };
    case "SelectAll": {
      const first = rows[0];
      const last = rows[rows.length - 1];
      return first === void 0 || last === void 0 ? { model: base } : selectRows(base, first.id, last.id);
    }
  }
};
var pasteText = (model, id, mode, start, end, text) => {
  let counter = model.nextId;
  const generate = () => {
    while (find(model.items, `${model.id}-${counter}`) !== void 0) counter += 1;
    return `${model.id}-${counter++}`;
  };
  const fragment = parseOutline(text, generate);
  const [first, ...rest] = fragment;
  const location = locate(model.items, id);
  const node = find(model.items, id);
  if (first === void 0 || location === void 0 || node === void 0) return { model };
  if (mode === "Rows") {
    const anchor = selectedRoots(model).at(-1) ?? id;
    const anchorLocation = locate(model.items, anchor) ?? location;
    const items2 = insertItems(
      model.items,
      anchorLocation.parentId,
      anchorLocation.index + 1,
      fragment
    );
    if (items2 === void 0) return { model };
    const pasted = commit({ ...model, nextId: counter }, items2, {
      announcement: `Pasted ${plural(fragment.length, "item")}.`
    });
    return selectRows(pasted, first.id, fragment.at(-1).id);
  }
  const before = node.text.slice(0, start);
  const after = node.text.slice(end);
  let items = updateItem(model.items, id, (current) => ({
    ...current,
    text: before + first.text,
    checked: before === "" && after === "" ? first.checked : current.checked,
    children: [...first.children, ...current.children],
    collapsed: first.children.length > 0 ? false : current.collapsed
  }));
  items = insertItems(items, location.parentId, location.index + 1, rest) ?? items;
  const inserted = [...walk(first.children), ...walk(rest)];
  const lastId = inserted.at(-1)?.id ?? id;
  const lastText = find(items, lastId)?.text ?? "";
  items = updateItem(items, lastId, (current) => ({ ...current, text: current.text + after }));
  const committed = commit({ ...model, nextId: counter }, items, {
    before: { id, start, end },
    announcement: `Pasted ${plural(fragment.length + walk(first.children).length, "item")}.`
  });
  return editText(committed, textFocus(lastId, lastText.length), items);
};
var textChange = (previous, next) => {
  let prefix = 0;
  while (prefix < previous.length && prefix < next.length && previous[prefix] === next[prefix]) {
    prefix += 1;
  }
  let suffix = 0;
  while (suffix < previous.length - prefix && suffix < next.length - prefix && previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]) {
    suffix += 1;
  }
  return previous.length - suffix;
};
var keepsHover = (message) => message._tag === "Hovered" || message._tag === "DismissedHover" || message._tag === "CompletedFocus" || message._tag === "Pressed" && message.action === "ShowInfo";
var keepsCompletion = (model, message) => {
  switch (message._tag) {
    case "ShowCompletions":
    case "RequestedCompletion":
    case "MovedCompletion":
    case "EditedText":
    case "Hovered":
    case "DismissedHover":
    case "CompletedFocus":
      return true;
    case "FocusedText":
      return message.id === model.completion?.id;
    case "Pressed":
      return message.action === "ShowInfo";
    default:
      return false;
  }
};
var MOVES = /* @__PURE__ */ new Set(["Indent", "Outdent", "MoveUp", "MoveDown"]);
var HISTORY = /* @__PURE__ */ new Set(["ClickedUndo", "ClickedRedo"]);
var moveOf = (model, message, after) => {
  if (message._tag === "Dropped") return { cause: "Drop", ids: model.drag?.ids ?? [] };
  if (message._tag !== "Pressed") return void 0;
  if (MOVES.has(message.action)) {
    const ids = model.mode === "Rows" && model.selection !== null ? selectedRoots(model) : [message.id];
    return { cause: message.action, ids };
  }
  const moved = reparented(model.items, after).map((move) => move.id);
  if (moved.length === 0) return void 0;
  return message.action === "Split" ? { cause: "Outdent", ids: roots(model.items, moved) } : { cause: "Merge", ids: roots(model.items, moved) };
};
var refuse = (model, message, reason) => {
  const caret = message._tag === "Pressed" && model.mode === "Text" ? { id: message.id, start: message.start, end: message.end } : model.focus;
  const kept = {
    ...model,
    focus: caret,
    drag: null,
    hover: null,
    completion: null,
    announcement: reason
  };
  return message._tag === "Pressed" && !keepsFocus(message.action) ? refocus(kept) : { model: kept };
};
var update = (model, message, policy = {}) => {
  const result = updateOutline(model, message);
  const travels = HISTORY.has(message._tag) || message._tag === "Pressed" && (message.action === "Undo" || message.action === "Redo");
  if ((policy.canMove !== void 0 || policy.isReadOnly !== void 0) && message._tag !== "Replace" && message._tag !== "Load" && !travels) {
    const move = moveOf(model, message, result.model.items);
    const reason = refusal(policy, model.items, result.model.items, move);
    if (reason !== void 0) return refuse(model, message, reason);
  }
  const hover = keepsHover(message) ? result.model.hover : null;
  const completion = keepsCompletion(model, message) ? result.model.completion : null;
  return hover === result.model.hover && completion === result.model.completion ? result : { ...result, model: { ...result.model, hover, completion } };
};
var completionCaret = (model, list) => model.focus?.id === list.id ? model.focus.end : list.to;
var narrowed = (model, id, before, after, caret) => {
  const list = model.completion;
  if (list === null || list.id !== id) return null;
  const next = Completion.track(list, before, after, caret);
  return next === void 0 || Completion.visible(next, after, caret).length === 0 ? null : { ...next, id };
};
var acceptCompletion = (model, index) => {
  const list = model.completion;
  const node = list === null ? void 0 : find(model.items, list.id);
  if (list === null || node === void 0) return { model: { ...model, completion: null } };
  const caret = completionCaret(model, list);
  const shown = Completion.visible(list, node.text, caret);
  const chosen = shown[index ?? Math.min(list.index, shown.length - 1)];
  if (chosen === void 0) return { model: { ...model, completion: null } };
  const accepted = Completion.accept(list, node.text, chosen);
  const items = updateItem(model.items, list.id, (current) => ({
    ...current,
    text: accepted.text
  }));
  const committed = commit(
    { ...model, completion: null, history: History.breakCoalescing(model.history) },
    items,
    {
      before: { id: list.id, start: caret, end: caret },
      announcement: `Inserted ${chosen.label}.`
    }
  );
  return editText(
    { ...committed, history: History.breakCoalescing(committed.history) },
    textFocus(list.id, accepted.caret),
    items
  );
};
var updateOutline = (model, message) => Message.match(message, {
  Pressed: ({ action, id, start, end, goalX }) => {
    const result = pressed(model, action, id, start, end, goalX);
    if (keepsFocus(action)) return { model: result.model };
    return result.commands === void 0 ? refocus(result.model) : result;
  },
  EditedText: ({ id, text, start, end, time }) => {
    const node = find(model.items, id);
    if (node === void 0 || node.text === text) {
      return { model: { ...model, focus: { id, start, end } } };
    }
    const offset = textChange(node.text, text);
    const items = updateItem(model.items, id, (current) => ({ ...current, text }));
    const paused = time - model.typedAt > TYPING_PAUSE;
    const history = paused ? History.breakCoalescing(model.history) : model.history;
    return {
      model: {
        ...commit({ ...model, history }, items, {
          before: textFocus(id, offset),
          coalescingKey: `text:${id}`
        }),
        typedAt: time,
        focus: { id, start, end },
        mode: "Text",
        selection: null,
        completion: narrowed(model, id, node.text, text, end)
      }
    };
  },
  FocusedText: ({ id, start, end }) => ({
    model: {
      ...model,
      mode: "Text",
      selection: null,
      focus: { id, start, end },
      history: model.focus?.id === id ? model.history : History.breakCoalescing(model.history)
    }
  }),
  PastedText: ({ id, mode, start, end, text }) => pasteText(model, id, mode, start, end, text),
  ToggledCollapsed: ({ id, recursive }) => {
    const node = find(model.items, id);
    if (node === void 0 || node.children.length === 0) return { model };
    const items = setCollapsed(model.items, id, !node.collapsed, recursive);
    const hidesFocus = !node.collapsed && model.focus !== null && ancestors(model.items, model.focus.id).includes(id);
    const next = {
      ...model,
      items,
      revision: model.revision + 1,
      focus: hidesFocus ? textFocus(id, node.text.length) : model.focus
    };
    return hidesFocus ? editText(next, next.focus, items) : { model: next };
  },
  ToggledChecked: ({ id }) => ({ model: toggleChecked(model, [id]) }),
  ClickedBullet: ({ id }) => hoist(model, id),
  SelectedRange: ({ anchorId, headId }) => {
    const rows = rowsOf(model);
    return rows.some((row) => row.id === anchorId) && rows.some((row) => row.id === headId) ? selectRows(model, anchorId, headId) : { model };
  },
  StartedDrag: ({ ids }) => {
    const dragged = roots(model.items, ids);
    if (dragged.length === 0) return { model };
    const rows = rowsOf(model);
    const selected = new Set(selectedIds(model, rows));
    const keepsSelection = dragged.every((id) => selected.has(id));
    return {
      model: {
        ...model,
        drag: { ids: dragged, target: null },
        ...keepsSelection ? {} : {
          mode: "Rows",
          selection: { anchorId: dragged[0], headId: dragged.at(-1) }
        }
      }
    };
  },
  MovedDrag: ({ target }) => model.drag === null ? { model } : { model: { ...model, drag: { ...model.drag, target } } },
  Dropped: () => {
    const drag = model.drag;
    const cleared = { ...model, drag: null };
    if (drag === null || drag.target === null) return { model: cleared };
    if (drag.target.refused === true) {
      return { model: { ...cleared, announcement: "Can't move there." } };
    }
    const items = moveItems(model.items, drag.ids, drag.target.placement);
    if (items === void 0) return { model: cleared };
    const moved = commit(cleared, items, {
      announcement: `Moved ${plural(drag.ids.length, "item")}.`
    });
    return selectRows(moved, drag.ids[0], drag.ids.at(-1));
  },
  CancelledDrag: () => ({ model: { ...model, drag: null } }),
  Hovered: ({ target }) => {
    const current = model.hover;
    if (target === null) return { model: current === null ? model : { ...model, hover: null } };
    return current !== null && current.source === "Pointer" && current.id === target.id && current.offset === target.offset ? { model } : { model: { ...model, hover: { ...target, source: "Pointer" } } };
  },
  DismissedHover: () => ({ model: model.hover === null ? model : { ...model, hover: null } }),
  RequestedCompletion: ({ id, start, end }) => find(model.items, id) === void 0 ? { model } : { model: { ...model, mode: "Text", selection: null, focus: { id, start, end } } },
  ShowCompletions: ({ id, from, to, items }) => {
    const node = find(model.items, id);
    const caret = model.focus?.id === id ? model.focus.end : void 0;
    if (node === void 0 || caret === void 0 || from < 0 || from > to || to > node.text.length || caret < from || caret > to) {
      return { model };
    }
    const list = { id, ...Completion.open(from, to, items) };
    const shown = Completion.visible(list, node.text, caret).length;
    if (shown === 0) return { model: { ...model, completion: null } };
    return {
      model: {
        ...model,
        completion: list,
        announcement: model.completion === null ? `${plural(shown, "suggestion")}.` : model.announcement
      }
    };
  },
  MovedCompletion: ({ delta }) => {
    const list = model.completion;
    const node = list === null ? void 0 : find(model.items, list.id);
    if (list === null || node === void 0) return { model };
    const count = Completion.visible(list, node.text, completionCaret(model, list)).length;
    return {
      model: { ...model, completion: { ...Completion.move(list, delta, count), id: list.id } }
    };
  },
  AcceptedCompletion: ({ index }) => acceptCompletion(model, index),
  FilledPlaceholder: ({ parentId, index, text, offset }) => {
    if (parentId !== null && find(model.items, parentId) === void 0) return { model };
    const { id, nextId } = newId(model);
    const opened = parentId === null ? model.items : setCollapsed(model.items, parentId, false);
    const items = insertItems(opened, parentId, index, [item(id, text)]);
    if (items === void 0) return { model };
    const caret = Math.max(0, Math.min(offset, text.length));
    return editText(
      { ...commit(model, items, { announcement: "Added an item." }), nextId },
      textFocus(id, caret),
      items
    );
  },
  DismissedCompletion: () => ({ model: { ...model, completion: null } }),
  Hoisted: ({ id }) => hoist(model, id),
  ClickedAdd: () => {
    const { id, nextId } = newId(model);
    const parentId = model.scopeId;
    const siblings = childrenOf(model.items, parentId) ?? [];
    const items = insertItems(model.items, parentId, siblings.length, [item(id)]);
    if (items === void 0) return { model };
    return editText({ ...commit(model, items), nextId }, textFocus(id, 0), items);
  },
  SetAllCollapsed: ({ collapsed }) => ({
    model: refold(
      model,
      foldScope(model, (items) => setAllCollapsed(items, collapsed)),
      collapsed ? "Collapsed all." : "Expanded all."
    )
  }),
  ExpandedToLevel: ({ level }) => ({
    model: refold(
      model,
      foldScope(model, (items) => expandToLevel(items, level)),
      `Showing ${plural(level, "level")}.`
    )
  }),
  ClickedUndo: () => travel(model, "Undo", model.focus),
  ClickedRedo: () => travel(model, "Redo", model.focus),
  Load: ({ items }) => ({
    model: {
      ...model,
      items,
      scopeId: null,
      mode: "Text",
      focus: null,
      selection: null,
      drag: null,
      history: History.init(),
      revision: model.revision + 1,
      nextId: Math.max(model.nextId, counterFor(model.id, items))
    }
  }),
  Replace: ({ items, announcement, coalescingKey }) => {
    if (items === model.items) return { model };
    const committed = commit(model, items, {
      announcement,
      ...coalescingKey === void 0 ? {} : { coalescingKey }
    });
    const exists = (id) => find(items, id) !== void 0;
    const selection = model.selection;
    return {
      model: {
        ...committed,
        scopeId: model.scopeId !== null && exists(model.scopeId) ? model.scopeId : null,
        focus: model.focus !== null && exists(model.focus.id) ? model.focus : null,
        ...selection !== null && !(exists(selection.anchorId) && exists(selection.headId)) ? { mode: "Text", selection: null } : {},
        nextId: Math.max(model.nextId, counterFor(model.id, items))
      }
    };
  },
  Reveal: ({ id }) => {
    const node = find(model.items, id);
    if (node === void 0) return { model };
    const items = ancestors(model.items, id).reduce(
      (nodes, ancestor) => setCollapsed(nodes, ancestor, false),
      model.items
    );
    const scopeId = isWithin(items, id, model.scopeId) && id !== model.scopeId ? model.scopeId : null;
    return editText(
      {
        ...model,
        scopeId,
        revision: items === model.items ? model.revision : model.revision + 1
      },
      textFocus(id, node.text.length),
      items
    );
  },
  CompletedFocus: () => ({ model })
});
var entriesOf = (scopeId, rows, placeholders) => {
  const first = rows[0]?.id;
  if (placeholders === void 0) {
    return rows.map((row) => ({ _tag: "Row", row, first: row.id === first }));
  }
  const children = /* @__PURE__ */ new Map();
  for (const row of rows) children.set(row.parentId, [...children.get(row.parentId) ?? [], row]);
  const result = [];
  const emit = (parentId, depth) => {
    const kids = children.get(parentId) ?? [];
    const ghosts = placeholders(parentId).map((placeholder) => ({
      placeholder,
      index: Math.max(0, Math.min(placeholder.index ?? kids.length, kids.length))
    }));
    const place = (index) => {
      for (const ghost of ghosts) {
        if (ghost.index === index) result.push({ _tag: "Placeholder", parentId, depth, ...ghost });
      }
    };
    kids.forEach((row, index) => {
      place(index);
      result.push({ _tag: "Row", row, first: row.id === first });
      if (!(row.hasChildren && row.collapsed)) emit(row.id, depth + 1);
    });
    place(kids.length);
  };
  emit(scopeId, 0);
  return result;
};
var quoted = (value) => `"${value.replace(/["\\]/g, "\\$&")}"`;
var mirrorSelector = (modelId, id) => `[data-outliner=${quoted(modelId)}] [data-outline-mirror=${quoted(id)}]`;
var validDiagnostics = (text, diagnostics) => (diagnostics ?? []).filter(
  (diagnostic) => diagnostic.from >= 0 && diagnostic.from <= diagnostic.to && diagnostic.to <= text.length
);
var shownHover = (model, inputs) => {
  const target = model.hover;
  if (target === null) return void 0;
  const node = find(model.items, target.id);
  if (node === void 0 || target.offset > node.text.length) return void 0;
  const answer = inputs.hover?.({ ...target, text: node.text }) ?? null;
  const diagnostics = diagnosticsAt(
    validDiagnostics(node.text, inputs.decorations?.[target.id]?.diagnostics),
    target.offset
  );
  const worst = mostSevere(diagnostics);
  const range = answer ?? worst;
  if (range === void 0 || answer?.content === null && diagnostics.length === 0) {
    return void 0;
  }
  const from = Math.max(0, Math.min(range.from, node.text.length));
  const to = Math.max(from, Math.min(range.to, node.text.length));
  return { id: target.id, from, to, diagnostics, content: answer?.content ?? null };
};
var untitled = (text) => text.split("\n")[0]?.trim() || "Untitled";
var triangle = (h) => h.svg(
  [h.Class("fw-outliner__triangle"), h.Attribute("viewBox", "0 0 10 10"), h.AriaHidden(true)],
  [h.path([h.Attribute("d", "M3 1.5 L8 5 L3 8.5 Z")], [])]
);
var shownCompletion = (model) => {
  const list = model.completion;
  const node = list === null ? void 0 : find(model.items, list.id);
  if (list === null || node === void 0) return void 0;
  const caret = model.focus?.id === list.id ? model.focus.end : list.to;
  const items = Completion.visible(list, node.text, caret);
  return items.length === 0 ? void 0 : {
    id: list.id,
    from: list.from,
    items,
    index: Math.min(list.index, items.length - 1),
    query: Completion.query(list, node.text, caret)
  };
};
var paint = (text, spans, diagnostics, hovered, h) => {
  let at = 0;
  const tokens = (spans ?? []).map((span) => {
    const token = { from: at, to: at + span.text.length, kind: span.kind };
    at = token.to;
    return token;
  });
  return segments(text, {
    tokens,
    diagnostics,
    hovered: hovered === void 0 ? [] : [hovered]
  }).map((piece) => {
    const kind = piece.covering.tokens[0]?.kind;
    const problem = mostSevere(piece.covering.diagnostics);
    const classes = [
      "fw-outliner__span",
      ...problem === void 0 ? [] : ["fw-text-diagnostic"],
      ...piece.covering.hovered.length > 0 ? ["fw-text-hovered"] : []
    ];
    return h.span(
      [
        h.Class(classes.join(" ")),
        ...kind === void 0 ? [] : [h.DataAttribute("kind", kind)],
        ...problem === void 0 ? [] : [h.DataAttribute("severity", problem.severity)]
      ],
      [piece.text]
    );
  });
};
var rowView = (model, row, selected, dragged, isFirst, inputs, hover, suggestions, readOnly, h) => {
  const ids = domIds(model.id);
  const isSelected = selected.has(row.id);
  const target = model.drag?.target;
  const indicator = target === null || target === void 0 ? void 0 : target.afterRowId === row.id ? "After" : target.afterRowId === null && isFirst ? "Before" : void 0;
  const label = untitled(row.text);
  const decoration = inputs.decorations?.[row.id];
  const spans = decoration?.spans !== void 0 && decoration.spans.map((span) => span.text).join("") === row.text ? decoration.spans : void 0;
  const diagnostics = validDiagnostics(row.text, decoration?.diagnostics);
  const hovered = hover?.id === row.id && hover.to > hover.from ? hover : void 0;
  const painted = spans !== void 0 || diagnostics.length > 0 || hovered !== void 0;
  const accessory = inputs.rowAccessory?.(row) ?? null;
  const folded = row.hasChildren && row.collapsed ? inputs.foldedView?.(row) ?? null : null;
  return h.keyed("div")(
    row.id,
    [
      h.Id(ids.row(row.id)),
      h.Class("fw-outliner__row"),
      h.Role("treeitem"),
      h.Tabindex(-1),
      h.AriaLevel(row.depth + 1),
      h.AriaSetsize(row.setSize),
      h.AriaPosinset(row.index + 1),
      h.AriaSelected(isSelected),
      h.AriaLabel(label),
      ...row.hasChildren ? [h.AriaExpanded(!row.collapsed)] : [],
      h.DataAttribute("outline-row", row.id),
      h.DataAttribute("selected", String(isSelected)),
      h.DataAttribute("checked", String(row.checked)),
      h.DataAttribute("dragging", String(dragged.has(row.id))),
      ...readOnly ? [h.DataAttribute("readonly", "true")] : [],
      ...decoration?.tone === void 0 ? [] : [h.DataAttribute("tone", decoration.tone)],
      h.Style({ "--fw-outliner-depth": String(row.depth) })
    ],
    [
      h.button(
        [
          h.Class("fw-outliner__toggle"),
          h.Type("button"),
          h.Tabindex(-1),
          h.AriaLabel(`${row.collapsed ? "Expand" : "Collapse"} ${label}`),
          h.Title(
            `${row.collapsed ? "Expand" : "Collapse"} \xB7 Option- or Alt-click for every level`
          ),
          h.DataAttribute("outline-toggle", row.id),
          h.DataAttribute("outline-control", "true"),
          h.DataAttribute(
            "state",
            !row.hasChildren ? "leaf" : row.collapsed ? "collapsed" : "expanded"
          ),
          h.Disabled(!row.hasChildren)
        ],
        [triangle(h)]
      ),
      h.span(
        [
          h.Class("fw-outliner__handle"),
          h.AriaHidden(true),
          h.Title("Drag to move \xB7 click to hoist"),
          h.DataAttribute("outline-handle", "true"),
          h.DataAttribute("folded", String(row.hasChildren && row.collapsed))
        ],
        [
          decoration?.marker === void 0 ? h.span([h.Class("fw-outliner__dot")], []) : h.span([h.Class("fw-outliner__marker")], [decoration.marker])
        ]
      ),
      ...inputs.showCheckboxes === true ? [
        h.button(
          [
            h.Class("fw-outliner__check"),
            h.Type("button"),
            h.Role("checkbox"),
            h.Tabindex(-1),
            h.AriaChecked(row.checked),
            h.AriaLabel(`Done: ${label}`),
            h.Disabled(readOnly),
            h.DataAttribute("outline-control", "true"),
            h.OnClick(Message.ToggledChecked({ id: row.id }))
          ],
          row.checked ? [
            h.svg(
              [h.Attribute("viewBox", "0 0 10 10"), h.AriaHidden(true)],
              [h.path([h.Attribute("d", "M2 5.2 4.1 7.3 8 2.8")], [])]
            )
          ] : []
        )
      ] : [],
      h.div(
        [h.Class("fw-outliner__cell"), h.DataAttribute("decorated", String(painted))],
        [
          h.textarea([
            h.Id(ids.text(row.id)),
            h.Class("fw-outliner__text"),
            h.Rows(1),
            h.Value(row.text),
            h.AriaLabel(`Item text, level ${row.depth + 1}`),
            h.Spellcheck(inputs.spellcheck ?? true),
            h.Readonly(readOnly),
            h.DataAttribute("outline-text", "true"),
            ...hover?.id === row.id ? [h.AriaDescribedBy(ids.hover)] : [],
            ...suggestions?.id === row.id ? [
              h.AriaAutocomplete("list"),
              h.AriaControls(ids.completion),
              h.AriaActiveDescendant(optionId(ids.completion, suggestions.index))
            ] : []
          ]),
          h.div(
            [
              h.Class("fw-outliner__mirror"),
              h.AriaHidden(true),
              h.DataAttribute("outline-mirror", row.id)
            ],
            [
              ...painted ? paint(row.text, spans, diagnostics, hovered, h) : [row.text],
              ...(spans === void 0 ? [] : decoration?.suffix ?? []).map(
                (span) => h.span(
                  [
                    h.Class("fw-outliner__span fw-outliner__suffix"),
                    h.DataAttribute("text-skip", "true"),
                    ...span.kind === void 0 ? [] : [h.DataAttribute("kind", span.kind)]
                  ],
                  [span.text]
                )
              ),
              h.span([h.DataAttribute("text-skip", "true")], ["\u200B"])
            ]
          )
        ]
      ),
      ...accessory === null ? [] : [h.div([h.Class("fw-outliner__accessory")], [accessory])],
      ...indicator === void 0 ? [] : [
        h.div(
          [
            h.Class("fw-outliner__drop"),
            h.DataAttribute("position", indicator),
            h.DataAttribute("refused", String(target?.refused === true)),
            h.AriaHidden(true),
            h.Style({ "--fw-outliner-drop-depth": String(target?.depth ?? 0) })
          ],
          []
        )
      ],
      ...folded === null ? [] : [
        h.div(
          [
            h.Class("fw-outliner__view"),
            h.Role("group"),
            h.AriaLabel(folded.label),
            h.Tabindex(-1),
            h.DataAttribute("outline-view", row.id)
          ],
          [folded.content]
        )
      ]
    ]
  );
};
var placeholderView = (entry, inputs, h) => {
  const { parentId, depth, index, placeholder } = entry;
  const text = placeholder.text ?? "";
  const caret = Math.max(0, Math.min(placeholder.caret ?? text.length, text.length));
  const name = `Add ${placeholder.label}`;
  return h.keyed("div")(
    `placeholder:${parentId ?? ""}:${placeholder.key}`,
    [
      h.Class("fw-outliner__row fw-outliner__placeholder"),
      h.Role("treeitem"),
      h.Tabindex(-1),
      h.AriaLevel(depth + 1),
      h.AriaLabel(name),
      h.DataAttribute("outline-placeholder", placeholder.key),
      ...parentId === null ? [] : [h.DataAttribute("parent", parentId)],
      h.DataAttribute("index", String(index)),
      h.DataAttribute("text", text),
      h.DataAttribute("caret", String(caret)),
      h.Style({ "--fw-outliner-depth": String(depth) })
    ],
    [
      h.span(
        [h.Class("fw-outliner__toggle"), h.DataAttribute("state", "leaf"), h.AriaHidden(true)],
        []
      ),
      h.button(
        [
          h.Class("fw-outliner__handle fw-outliner__plus"),
          h.Type("button"),
          h.Tabindex(-1),
          h.AriaLabel(name),
          h.Title(name),
          h.DataAttribute("outline-control", "true"),
          h.OnClick(
            Message.FilledPlaceholder({
              parentId,
              index,
              key: placeholder.key,
              text,
              offset: caret
            })
          )
        ],
        ["+"]
      ),
      h.div(
        [h.Class("fw-outliner__cell")],
        [
          h.textarea([
            h.Class("fw-outliner__text"),
            h.Rows(1),
            h.Value(""),
            h.Placeholder(placeholder.label),
            h.AriaLabel(name),
            h.Spellcheck(inputs.spellcheck ?? true),
            h.DataAttribute("placeholder-text", "true")
          ])
        ]
      )
    ]
  );
};
var breadcrumbs = (model, h) => {
  if (model.scopeId === null) return [];
  const scope = find(model.items, model.scopeId);
  if (scope === void 0) return [];
  const trail = ancestors(model.items, model.scopeId);
  const crumb = (id, text) => h.li(
    [],
    [
      h.button(
        [h.Class("fw-outliner__crumb"), h.Type("button"), h.OnClick(Message.Hoisted({ id }))],
        [text]
      )
    ]
  );
  return [
    h.nav(
      [h.Class("fw-outliner__crumbs"), h.AriaLabel("Hoisted item path")],
      [
        h.ol(
          [],
          [
            crumb(null, "Outline"),
            ...trail.map((id) => crumb(id, untitled(find(model.items, id)?.text ?? "")))
          ]
        )
      ]
    ),
    h.h2([h.Class("fw-outliner__title")], [untitled(scope.text)])
  ];
};
var view = defineView((model, inputs, h) => {
  const ids = domIds(model.id);
  const rows = rowsOf(model);
  const hover = rows.some((row) => row.id === model.hover?.id) ? shownHover(model, inputs) : void 0;
  const suggestions = rows.some((row) => row.id === model.completion?.id) ? shownCompletion(model) : void 0;
  const selected = new Set(model.mode === "Rows" ? selectedIds(model, rows) : []);
  const isReadOnly = inputs.isReadOnly;
  const locked = new Set(
    isReadOnly === void 0 ? [] : walk(model.items).filter((node) => isReadOnly(node)).map((node) => node.id)
  );
  const dragged = new Set(model.drag?.ids ?? []);
  for (const row of rows) {
    if (row.parentId !== null && dragged.has(row.parentId)) dragged.add(row.id);
  }
  return h.div(
    [
      h.Class("fw-outliner"),
      h.DataAttribute("outliner", model.id),
      h.DataAttribute("mode", model.mode),
      h.DataAttribute("dragging", String(model.drag !== null)),
      h.Style({ "--fw-outliner-indent": `${INDENT}px` }),
      { _tag: "Prop", key: MODEL_PROPERTY, value: model },
      {
        _tag: "Prop",
        key: POLICY_PROPERTY,
        value: { canMove: inputs.canMove, isReadOnly: inputs.isReadOnly }
      },
      h.OnMount(Surface())
    ],
    [
      ...inputs.showBreadcrumbs === false ? [] : breadcrumbs(model, h),
      h.div(
        [
          h.Id(ids.tree),
          h.Class("fw-outliner__tree"),
          h.Role("tree"),
          h.AriaLabel(inputs.label ?? "Outline"),
          h.Attribute("aria-multiselectable", "true"),
          h.Tabindex(-1)
        ],
        entriesOf(model.scopeId, rows, inputs.placeholders).map(
          (entry) => entry._tag === "Placeholder" ? placeholderView(entry, inputs, h) : rowView(
            model,
            entry.row,
            selected,
            dragged,
            entry.first,
            inputs,
            hover,
            suggestions,
            locked.has(entry.row.id),
            h
          )
        )
      ),
      ...rows.length === 0 ? [
        h.button(
          [
            h.Id(ids.add),
            h.Class("fw-outliner__add"),
            h.Type("button"),
            h.OnClick(Message.ClickedAdd())
          ],
          [inputs.emptyLabel ?? (model.scopeId === null ? "Start an outline" : "Add an item")]
        )
      ] : [],
      ...suggestions === void 0 ? [] : [
        CompletionPopup.view(
          {
            id: ids.completion,
            items: suggestions.items,
            index: suggestions.index,
            query: suggestions.query,
            anchor: {
              selector: mirrorSelector(model.id, suggestions.id),
              offset: suggestions.from
            },
            onChoose: (index) => Message.AcceptedCompletion({ index })
          },
          h
        )
      ],
      ...hover === void 0 ? [] : [
        HoverPopup.view(
          {
            id: ids.hover,
            anchor: { selector: mirrorSelector(model.id, hover.id), offset: hover.from },
            diagnostics: hover.diagnostics,
            content: hover.content
          },
          h
        )
      ],
      h.div([h.Class("fw-outliner__live"), h.AriaLive("polite")], [model.announcement])
    ]
  );
});

// src/outliner.ts
var Message2 = Message;
var Model2 = Model;

export { Action, Document, Item, Items, outliner_exports as Outliner, ancestors, childrenOf, dropTarget, expandToLevel, find, indent, insertItems, isWithin, item, keepsReadOnly, locate, mergeIntoPrevious, mergeNext, moveDown, moveItems, moveUp, outdent, parseOutline, removeItems, reparented, resolveKey, roots, selectedIds, selectedRoots, serializeOutline, setAllCollapsed, setCollapsed, shortcutHelp, split, updateItem, visibleRows, walk };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map